// File: src/pages/TrialDetail.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase, supabaseUrl, supabaseAnonKey } from '../supabaseClient';
import { ArrowLeft, Loader2, Zap, Download, Copy, Check, Clock, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import FollowButton from '../components/FollowButton';
import JurySummonsButton from '../components/JurySummonsButton';
import VerdictMemeFrame from '../components/VerdictMemeFrame';
import ClaimVoteModal from '../components/ClaimVoteModal';
import JurySeatProgress from '../components/JurySeatProgress';
import OpenInAppBanner from '../components/OpenInAppBanner';
import ReportModal from '../components/ReportModal';
import ActionPromptModal from '../components/ActionPromptModal';
import { getAnonId, getExistingAnonId } from '../utils/anonId';
import { getShareUrl } from '../utils/deepLinkConfig';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { consumeCrewDailyChallengePending } from '../utils/crewDailyChallenge';
import { recordReferralMilestone, trackReferralAttribution } from '../utils/referrals';
import { updateStreakForActivity } from '../utils/streaks';
import { deriveTrialVerdictRenderData, getCanonicalTrialDetailVerdictUrl } from '../utils/verdictAssets';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';

const HIDDEN_TRIALS_KEY = 'gweffit_hidden_trial_ids';
const HIDDEN_COLD_BANNER_SESSION_KEY = 'gweffit_trial_cold_banner_dismissed_session';
const PENDING_TRIAL_VOTE_KEY = 'gweffit_trial_pending_vote';

function getHiddenTrialIds() {
  try {
    const raw = localStorage.getItem(HIDDEN_TRIALS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.map((v) => String(v)) : [];
  } catch {
    return [];
  }
}

function hideTrialLocally(trialId) {
  if (!trialId) return;
  try {
    const next = Array.from(new Set([...getHiddenTrialIds(), String(trialId)]));
    localStorage.setItem(HIDDEN_TRIALS_KEY, JSON.stringify(next.slice(-500)));
  } catch {}
}

// Helper: format seconds into display string
const formatTime = (seconds) => {
  if (seconds == null || seconds < 0) return null;
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
};

function shouldRedirectGuestVoteToAuth(voteError) {
  const code = String(voteError?.code || '').toLowerCase();
  const message = String(voteError?.message || '').toLowerCase();
  const details = String(voteError?.details || '').toLowerCase();
  const hint = String(voteError?.hint || '').toLowerCase();
  const combined = `${message} ${details} ${hint}`.trim();

  if (code === '42501') return true;
  if (combined.includes('row-level security')) return true;
  if (combined.includes('permission denied')) return true;
  if (combined.includes('not authenticated')) return true;
  if (combined.includes('missing authorization')) return true;
  if (combined.includes('jwt')) return true;
  if (combined.includes('authenticated')) return true;

  return false;
}

function getGuestVoteErrorMessage(voteError) {
  const reason = String(voteError?.reason || '').toLowerCase();
  const code = String(voteError?.code || '').toLowerCase();
  const message = String(voteError?.message || '').toLowerCase();

  if (reason === 'already_voted') {
    return 'You already voted on this trial!';
  }

  if (reason === 'voting_closed') {
    return 'Voting is closed on this trial.';
  }

  if (code === '23514' || message.includes('trial_not_open') || message.includes('trial_vote_window_closed')) {
    if (message.includes('creator_jury_vote_forbidden')) {
      return "This is your call. You can't cast a separate jury vote.";
    }
    return 'Voting is closed on this trial.';
  }

  return 'Vote failed. Please try again.';
}

function getDownloadExtensionForMimeType(mimeType) {
  const normalized = String(mimeType || '').toLowerCase();
  if (normalized.includes('png')) return 'png';
  if (normalized.includes('jpeg') || normalized.includes('jpg')) return 'jpg';
  if (normalized.includes('webp')) return 'webp';
  return 'png';
}

async function copyTextReliably(text) {
  const value = String(text || '');
  if (!value) throw new Error('empty_copy_text');

  if (Capacitor.isNativePlatform?.()) {
    await Clipboard.write({ string: value });
    return;
  }

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!ok) throw new Error('exec_copy_failed');
}

function TrialVerdictHeroCard({
  cardId = null,
  row,
  verdictLine,
  verdictImageUrl,
  winner,
  pctA,
  pctB,
  total,
  suppressPremiumMemeLocalOverlay,
  hidden = false,
}) {
  if (!row) return null;

  const loserLabel = winner === 'Tie'
    ? `${row.side_a} vs ${row.side_b}`
    : (winner === row.side_a ? row.side_b : row.side_a);
  const winnerPct = winner === row.side_b ? pctB : pctA;

  return (
    <div
      {...(cardId ? { id: cardId } : {})}
      style={{
        ...(hidden ? {
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
        } : {}),
        width: '1080px',
        background: 'linear-gradient(180deg, #0f172a 0%, #111827 52%, #020617 100%)',
        color: 'white',
        padding: '48px',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          borderRadius: '36px',
          overflow: 'hidden',
          border: '1px solid rgba(148,163,184,0.18)',
          background: 'rgba(2,6,23,0.78)',
          boxShadow: '0 24px 90px rgba(0,0,0,0.38)',
        }}
      >
        <div style={{ padding: '32px 36px 24px' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(196,181,253,0.86)' }}>
            Final Verdict
          </div>
          <div style={{ marginTop: '14px', fontSize: '60px', lineHeight: 1.02, fontWeight: 900, color: '#f8fafc' }}>
            {row.title}
          </div>
        </div>

        <div style={{ padding: '0 24px' }}>
          <VerdictMemeFrame
            id={row.id}
            title={row.title}
            sideA={row.side_a}
            sideB={row.side_b}
            verdictLine={verdictLine || null}
            imageUrl={verdictImageUrl}
            emoji={row.image_emoji}
            winnerLabel={winner || 'Tie'}
            loserLabel={loserLabel}
            isClose={Math.abs(pctA - 50) <= 5}
            isLandslide={Math.abs(pctA - 50) >= 25}
            heightClass="h-[880px]"
            renderState="ready"
            showEmoji={false}
            surface="trial-detail-export"
            suppressLocalOverlayText={suppressPremiumMemeLocalOverlay}
          />
        </div>

        <div
          style={{
            padding: '28px 36px 34px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '24px',
          }}
        >
          <div style={{ maxWidth: '72%' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(148,163,184,0.88)' }}>
              The Verdict
            </div>
            <div style={{ marginTop: '12px', fontSize: '58px', lineHeight: 1.04, fontWeight: 900, color: '#f8fafc' }}>
              {verdictLine || (winner ? `${winner} took this one.` : 'The result is in.')}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '26px', fontWeight: 800, color: '#c4b5fd' }}>
              {winner === 'Tie' ? 'Tie' : winner}
            </div>
            <div style={{ marginTop: '8px', fontSize: '22px', color: 'rgba(226,232,240,0.84)' }}>
              {total} votes
            </div>
            {winner !== 'Tie' && (
              <div style={{ marginTop: '6px', fontSize: '20px', color: 'rgba(148,163,184,0.92)' }}>
                {Math.round(winnerPct)}% of the jury
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getVisibleTrialVerdictUrl(trialOrVerdictData) {
  const verdictData =
    trialOrVerdictData?.verdict_data && typeof trialOrVerdictData.verdict_data === 'object'
      ? trialOrVerdictData.verdict_data
      : (trialOrVerdictData && typeof trialOrVerdictData === 'object' ? trialOrVerdictData : null);

  if (!verdictData) return null;

  return (
    verdictData.share_card_url ||
    verdictData.final_image_url ||
    verdictData.verdict_image_url ||
    verdictData.image_url ||
    verdictData.aiImage ||
    verdictData.ai_image ||
    verdictData.meme_image_url ||
    verdictData.background_image_url ||
    null
  );
}

function isBuiltVerdictCardAsset(url) {
  const normalized = String(url || '').toLowerCase();
  return normalized.includes('/verdict-card.svg') || normalized.includes('/premium-verdict-card.svg');
}

function buildCrewTrialSignals(trial, crews, crewMembers, voteRows, currentAppUserId) {
  if (!trial?.id || !Array.isArray(crews) || crews.length === 0) return [];

  const latestVotesByUser = new Map();
  for (const vote of voteRows || []) {
    const userId = vote?.user_id;
    if (!userId) continue;
    const nextTs = new Date(vote.created_at || 0).getTime();
    const prev = latestVotesByUser.get(userId);
    const prevTs = prev ? new Date(prev.created_at || 0).getTime() : -Infinity;
    if (!prev || nextTs >= prevTs) latestVotesByUser.set(userId, vote);
  }

  const membersByCrewId = new Map();
  for (const member of crewMembers || []) {
    const crewId = member?.crew_id;
    const userId = member?.user_id;
    if (!crewId || !userId) continue;
    const next = membersByCrewId.get(crewId) || [];
    next.push(userId);
    membersByCrewId.set(crewId, next);
  }

  const finalWinnerSide = Number(trial.votes_a || 0) === Number(trial.votes_b || 0)
    ? null
    : Number(trial.votes_a || 0) > Number(trial.votes_b || 0) ? 'A' : 'B';

  return crews
    .map((crew) => {
      const memberIds = membersByCrewId.get(crew.id) || [];
      let votesA = 0;
      let votesB = 0;
      let userChoice = null;

      memberIds.forEach((memberId) => {
        const vote = latestVotesByUser.get(memberId);
        if (!vote) return;
        if (vote.vote_side === 'A') votesA += 1;
        if (vote.vote_side === 'B') votesB += 1;
        if (memberId === currentAppUserId) userChoice = vote.vote_side;
      });

      const totalCrewVotes = votesA + votesB;
      const crewLeader = votesA === votesB ? null : (votesA > votesB ? 'A' : 'B');
      const crewLeaderLabel = crewLeader === 'A' ? trial.side_a : crewLeader === 'B' ? trial.side_b : null;
      const runnerUpLabel = crewLeader === 'A' ? trial.side_b : crewLeader === 'B' ? trial.side_a : null;
      const margin = Math.abs(votesA - votesB);
      const yourVoteCanSwing = !userChoice && totalCrewVotes > 0 && margin <= 1;
      const crewWon = finalWinnerSide && crewLeader ? finalWinnerSide === crewLeader : null;

      let liveLine = null;
      if (totalCrewVotes > 0) {
        if (yourVoteCanSwing) {
          liveLine = `Your vote can still swing ${crew.name} toward ${crewLeaderLabel || 'either side'}.`;
        } else if (crewLeaderLabel) {
          liveLine = `${crew.name} is backing ${crewLeaderLabel}.`;
        } else {
          liveLine = `${crew.name} is split right now.`;
        }
      }

      let resultLine = null;
      if (totalCrewVotes === 0) {
        resultLine = `${crew.name} never really picked a side before the verdict landed.`;
      } else if (crewWon === true) {
        resultLine = `${crew.name} backed ${crewLeaderLabel} and won this one.`;
      } else if (crewWon === false && crewLeaderLabel && runnerUpLabel) {
        resultLine = `${crew.name} backed ${crewLeaderLabel} and lost to ${runnerUpLabel}.`;
      } else {
        resultLine = `${crew.name} finished split on this one.`;
      }

      return {
        id: crew.id,
        name: crew.name || 'Your crew',
        emoji: crew.emoji || '👥',
        totalCrewVotes,
        votesA,
        votesB,
        crewLeader,
        crewLeaderLabel,
        yourVoteCanSwing,
        userChoice,
        crewWon,
        liveLine,
        resultLine,
      };
    })
    .filter((crew) => crew.totalCrewVotes > 0 || crew.crewWon != null)
    .sort((a, b) => {
      if (Boolean(a.yourVoteCanSwing) !== Boolean(b.yourVoteCanSwing)) return a.yourVoteCanSwing ? -1 : 1;
      return (b.totalCrewVotes || 0) - (a.totalCrewVotes || 0);
    });
}

export default function TrialDetail() {
  const { id } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const sourceParam = urlParams.get('source');
  const notificationTypeParam = urlParams.get('notification_type');
  const comebackRoleParam = String(
    urlParams.get('role')
    || location.state?.comebackRole
    || ''
  ).toLowerCase();
  const comebackSource = String(
    location.state?.comebackSource
    || (notificationTypeParam === 'verdict_ready' ? 'push' : '')
    || sourceParam
    || ''
  ).toLowerCase();
  const createdParam = urlParams.get('created') === '1';
  const backHref = sourceParam === 'vault'
    ? '/verdict-vault'
    : '/';
  const pageShellStyle = {
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
  };

  // Diagnostic logging for deep link debugging
  useEffect(() => {
    if (id) {
      console.log('[TrialDetail] Loaded with trialId:', id, '| URL:', window.location.href);
    }
  }, [id]);

  const [row, setRow] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [copied, setCopied] = useState(false);
  const [surfaceNotice, setSurfaceNotice] = useState(null);

  const [currentUserId, setCurrentUserId] = useState(() => {
    // Seed from cache immediately so the follow button renders on first paint
    // for anonymous app users who may not have a full Supabase auth session.
    try {
      return JSON.parse(localStorage.getItem('gweffit_user_cache') || 'null')?.id || null;
    } catch { return null; }
  });
  const [canModerationView, setCanModerationView] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimModalMode, setClaimModalMode] = useState('post_vote');
  const [lastVoteSide, setLastVoteSide] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null); // Local countdown state
  const [memeRecoveryState, setMemeRecoveryState] = useState('idle');
  const [memeRecoveryError, setMemeRecoveryError] = useState(null);
  const [reopeningTieBreaker, setReopeningTieBreaker] = useState(false);
  const [showTieBreakerPrompt, setShowTieBreakerPrompt] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('spam');
  const [reportNote, setReportNote] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [optimisticJuryFilled, setOptimisticJuryFilled] = useState(null);
  const [crewSignals, setCrewSignals] = useState([]);
  const [creatorCallSide, setCreatorCallSide] = useState(null);
  const [premiumMemeImageOverride, setPremiumMemeImageOverride] = useState(null);
  const [verdictCacheRow, setVerdictCacheRow] = useState(null);
  const [showColdTrafficBanner, setShowColdTrafficBanner] = useState(() => {
    try {
      localStorage.removeItem('gweffit_trial_cold_banner_dismissed');
      return sessionStorage.getItem(HIDDEN_COLD_BANNER_SESSION_KEY) !== '1';
    } catch {
      return true;
    }
  });

  const cardRef = useRef(null);
  const rowRef = useRef(null);
  const memeRecoveryInFlightRef = useRef(new Set());
  const verdictCacheAppliedRef = useRef(new Set());
  const referralCaptureSignatureRef = useRef(null);
  const pendingVoteAutoSubmitRef = useRef(false);

  useEffect(() => {
    rowRef.current = row;
  }, [row]);

  useEffect(() => {
    if (!surfaceNotice?.message) return undefined;
    const timeout = window.setTimeout(() => setSurfaceNotice(null), 2600);
    return () => window.clearTimeout(timeout);
  }, [surfaceNotice]);

  const showSurfaceNotice = useCallback((message, tone = 'success') => {
    setSurfaceNotice({ message, tone });
  }, []);

  useEffect(() => {
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const inviterUserId = params.get('ref');
    const sourceType = params.get('ref_src');
    const sourceId = params.get('ref_id');

    if (!inviterUserId || !sourceType) return;

    const signature = [
      id || '',
      inviterUserId,
      sourceType,
      sourceId || '',
      currentUserId || 'anon',
    ].join('|');

    if (referralCaptureSignatureRef.current === signature) return;
    referralCaptureSignatureRef.current = signature;

    let cancelled = false;
    (async () => {
      try {
        await trackReferralAttribution({
          inviterUserId,
          sourceType,
          sourceId,
          invitedUserId: currentUserId || null,
        });
      } catch (captureErr) {
        if (!cancelled) {
          console.warn('⚠️ Failed to capture trial detail referral attribution:', captureErr?.message || captureErr);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, currentUserId]);

  useEffect(() => {
    setPremiumMemeImageOverride(null);
    setMemeRecoveryState('idle');
    setMemeRecoveryError(null);
    setVerdictCacheRow(null);
  }, [id]);
  const verdictCacheInFlightRef = useRef(new Set());

  const handleGeneratePremiumMeme = useCallback(async () => {
    if (!row?.id || memeRecoveryInFlightRef.current.has(row.id)) return;
    const rowCreatorId = row?.created_by || row?.creator_id || null;
    const creatorOwnsTrial = Boolean(currentUserId && rowCreatorId && String(currentUserId) === String(rowCreatorId));
    if (!creatorOwnsTrial) {
      setMemeRecoveryState('failed');
      setMemeRecoveryError('Only the creator can make the premium card for this trial.');
      return;
    }
    memeRecoveryInFlightRef.current.add(row.id);
    setMemeRecoveryState('loading');
    setMemeRecoveryError(null);
    console.log('[verdict] premium_meme_triggered', {
      trial_id: row.id,
      source: 'trial_detail_manual',
      current_user_id: currentUserId || null,
    });

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || null;
      console.log('[verdict] premium_meme_request_started', {
        trial_id: row.id,
        has_access_token: Boolean(accessToken),
        current_user_id: currentUserId || null,
        source: 'trial_detail_manual',
      });
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-verdict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ trialId: row.id, forceImageRegenerate: true }),
      });
      const rawBody = await response.text();
      let data = null;
      try {
        data = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        data = null;
      }
      if (!response.ok) {
        const failureReason =
          response.status === 403 || data?.error === 'forbidden_premium_generation'
            ? 'Only the creator can make the premium card for this trial.'
            : response.status === 401 || data?.error === 'unauthorized_premium_generation'
              ? 'Sign in as the creator to make the premium card for this trial.'
              :
          data?.final_image_source === 'text_only'
            ? 'Premium meme unavailable right now. Please try again in a bit.'
            : data?.error || 'Premium meme unavailable right now.';
        console.log('[verdict] premium_meme_backend_error', {
          trial_id: row.id,
          status: response.status,
          error: data?.error || null,
          final_image_source: data?.final_image_source || null,
          image_source: data?.image_source || null,
          gate_issues: data?.gate_issues || [],
          retry_reason: data?.retry_reason || [],
          raw_body: rawBody || null,
          source: 'trial_detail_manual',
        });
        throw new Error(failureReason);
      }

      const imageUrl = data?.image_url || null;
      const returnedImageSource = String(data?.image_source || data?.final_image_source || '').toLowerCase();
      if (!imageUrl || returnedImageSource !== 'ai_primary') {
        setMemeRecoveryState('failed');
        setMemeRecoveryError(
          !imageUrl
            ? 'image_unavailable'
            : `premium_not_available:${returnedImageSource || 'unknown'}`
        );
        console.log('[verdict] premium_meme_failed', {
          trial_id: row.id,
          reason: !imageUrl ? 'no_image_url' : `non_premium_result:${returnedImageSource || 'unknown'}`,
        });
        return;
      }

      const responseVerdict = {
        ...((rowRef.current?.verdict_data && typeof rowRef.current.verdict_data === 'object') ? rowRef.current.verdict_data : {}),
        image_url: imageUrl,
        ai_image: imageUrl,
        aiImage: imageUrl,
        share_card_url: data?.share_card_url || imageUrl,
        composed_share_card_url: data?.composed_share_card_url || data?.share_card_url || imageUrl,
        background_image_url: data?.background_image_url || rowRef.current?.verdict_data?.background_image_url || null,
        premium_verdict_line: data?.premium_verdict_line || data?.full_verdict_line || null,
        premium_verdict_line_full: data?.premium_verdict_line_full || data?.full_verdict_line || null,
        premium_verdict_line_short: data?.premium_verdict_line_short || data?.overlay_line || null,
        premium_overlay_line: data?.premium_overlay_line || data?.overlay_line || null,
        premium_share_line: data?.premium_share_line || data?.full_verdict_line || null,
        verdict_source: rowRef.current?.verdict_data?.verdict_source || 'ai',
        image_source: returnedImageSource,
      };
      const { data: trialReadback } = await supabase
        .from('trials')
        .select('verdict_data')
        .eq('id', row.id)
        .maybeSingle();
      const authoritativeVerdict =
        trialReadback?.verdict_data && typeof trialReadback.verdict_data === 'object'
          ? trialReadback.verdict_data
          : responseVerdict;
      verdictCacheAppliedRef.current.add(row.id);
      const canonicalPremium = getCanonicalTrialDetailVerdictUrl({ id: row.id, verdict_data: authoritativeVerdict });
      setPremiumMemeImageOverride(canonicalPremium.url || imageUrl || null);
      setVerdictCacheRow((prev) => ({
        ...(prev || {}),
        image_url: data?.image_url || authoritativeVerdict?.image_url || null,
        internet_punchline: authoritativeVerdict?.verdict_line || authoritativeVerdict?.full_verdict_line || null,
        roast_punchline: authoritativeVerdict?.verdict_line || authoritativeVerdict?.full_verdict_line || null,
        sports_punchline: authoritativeVerdict?.verdict_line || authoritativeVerdict?.full_verdict_line || null,
        status: 'completed',
      }));
      setRow((prev) => (prev?.id === row.id ? { ...prev, verdict_data: authoritativeVerdict } : prev));
      setMemeRecoveryState('succeeded');
      console.log('[verdict] premium_meme_source_selected', {
        trial_id: row.id,
        source: 'trial_detail_manual',
        image_source: authoritativeVerdict?.image_source || returnedImageSource,
        selected_image_url: canonicalPremium.url || imageUrl || null,
        selected_image_source: canonicalPremium.source || null,
        share_card_url: authoritativeVerdict?.share_card_url || null,
        composed_share_card_url: authoritativeVerdict?.composed_share_card_url || null,
        background_image_url: authoritativeVerdict?.background_image_url || null,
        verdict_line: authoritativeVerdict?.verdict_line || authoritativeVerdict?.full_verdict_line || null,
      });
      console.log('[verdict] premium_meme_succeeded', { trial_id: row.id, source: 'trial_detail_manual' });
    } catch (err) {
      setMemeRecoveryState('failed');
      setMemeRecoveryError(err?.message || 'premium_meme_error');
      console.log('[verdict] premium_meme_failed', {
        trial_id: row?.id || id,
        reason: err?.message || String(err),
        source: 'trial_detail_manual',
      });
    } finally {
      memeRecoveryInFlightRef.current.delete(row?.id);
    }
  }, [row, id, currentUserId]);

  const isGenericFallbackPunchline = useCallback((value) => {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized.startsWith('jury rules for');
  }, []);

  const hasAuthoritativeVerdict = useCallback((trialRow) => {
    if (!trialRow?.verdict_data || typeof trialRow.verdict_data !== 'object') return false;
    const source = String(trialRow.verdict_data.verdict_source || '').toLowerCase();
    if (source === 'ai' || source === 'non_ai_fallback') return true;
    // Backward compatibility: older finalized rows may not have verdict_source populated.
    return Boolean(
      trialRow.verdict_data.punchline ||
      trialRow.verdict_data.verdictType ||
      trialRow.verdict_data.verdict_type ||
      trialRow.verdict_data.winner ||
      trialRow.verdict_data.winnerPct ||
      trialRow.verdict_data.winner_pct
    );
  }, []);

  const getVerdictImageUrl = useCallback((trialRow) => {
    return getCanonicalTrialDetailVerdictUrl(trialRow).url;
  }, []);

  const getVisibleVerdictImageUrl = useCallback((trialRow) => {
    return getVisibleTrialVerdictUrl(trialRow);
  }, []);

  // Compute timeLeft from expires_at
  const computeTimeLeft = useCallback((expiresAt) => {
    if (!expiresAt) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⏱️ TrialDetail: expires_at missing or invalid');
      }
      return null;
    }
    const expiresMs = new Date(expiresAt).getTime();
    if (isNaN(expiresMs)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⏱️ TrialDetail: expires_at is invalid date:', expiresAt);
      }
      return null;
    }
    return Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
  }, []);

  // Local countdown timer - ticks every second independently of parent feed
  useEffect(() => {
    if (!row?.expires_at) return;

    // Initial compute
    setTimeLeft(computeTimeLeft(row.expires_at));

    // Tick every second
    const interval = setInterval(() => {
      setTimeLeft(computeTimeLeft(row.expires_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [row?.expires_at, computeTimeLeft]);

  // Recompute immediately when app resumes or page becomes visible
  // Note: document.visibilitychange works in both web and Capacitor WebView
  useEffect(() => {
    if (!row?.expires_at) return;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setTimeLeft(computeTimeLeft(row.expires_at));
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [row?.expires_at, computeTimeLeft]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && Capacitor.isNativePlatform() && typeof window !== 'undefined') {
      console.log('[trial] top_actions_layout', { width: window.innerWidth });
    }
  }, [id]);

  useEffect(() => {
    let alive = true;

    const syncAuthState = async (session = null) => {
      const resolvedSession = session ?? (await supabase.auth.getSession()).data?.session ?? null;
      const authUserId = resolvedSession?.user?.id || null;

      if (!authUserId) {
        if (alive) {
          setCurrentUserId((prev) => prev || null);
          setCanModerationView(false);
        }
        return;
      }

      try {
        const cfg = await getRuntimeConfig();
        const runtimeAdmins = new Set((cfg?.adminAuthUserIds || []).map((v) => String(v)));
        if (runtimeAdmins.has(String(authUserId))) {
          if (alive) setCanModerationView(true);
        } else {
          const { data: adminRow } = await supabase
            .from('moderation_admins')
            .select('auth_user_id')
            .eq('auth_user_id', authUserId)
            .eq('is_active', true)
            .maybeSingle();
          if (alive) setCanModerationView(Boolean(adminRow?.auth_user_id));
        }
      } catch {
        if (alive) setCanModerationView(false);
      }

      const { data: userRow } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (userRow?.id && alive) setCurrentUserId(userRow.id);
    };

    syncAuthState();

    const { data: authSubscription } = supabase.auth.onAuthStateChange((_event, session) => {
      syncAuthState(session);
    });

    return () => {
      alive = false;
      authSubscription?.subscription?.unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!currentUserId || pendingVoteAutoSubmitRef.current) return;

    let pendingVote = null;
    try {
      pendingVote = JSON.parse(localStorage.getItem(PENDING_TRIAL_VOTE_KEY) || 'null');
    } catch {
      pendingVote = null;
    }

    if (!pendingVote || String(pendingVote.trialId) !== String(id) || !pendingVote.side) return;

    pendingVoteAutoSubmitRef.current = true;
    console.log('[vote_return_after_auth]', {
      trial_id: id,
      side: pendingVote.side,
      auth_user_id_present: true,
      app_user_id: currentUserId,
    });

    setShowClaimModal(false);
    handleVote(pendingVote.side, { skipGuestFallback: true, source: 'return_after_auth' })
      .finally(() => {
        try { localStorage.removeItem(PENDING_TRIAL_VOTE_KEY); } catch {}
        pendingVoteAutoSubmitRef.current = false;
      });
  }, [currentUserId, id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleVote(side, options = {}) {
    if (!row) return;
    setVoting(true);

    const isAuthenticated = !!currentUserId;
    const anonId = getAnonId();
    const source = options?.source || 'click';

    console.log('[vote_click_started]', {
      trial_id: id,
      side,
      source,
    });
    console.log('[vote_auth_state]', {
      trial_id: id,
      side,
      is_authenticated: isAuthenticated,
      current_user_id: currentUserId,
      has_anon_id: Boolean(anonId),
      source,
    });

    // 📊 Track vote attempt (before we know if it succeeds)
    if (window.trackEvent) {
      window.trackEvent('vote_attempted', {
        trial_id: id,
        side,
        is_authenticated: isAuthenticated,
      });
    }

    try {
      if (isAuthenticated) {
        const { data: sessionData } = await supabase.auth.getSession();
        const authUserId = sessionData?.session?.user?.id || null;
        let canonicalUserId = currentUserId || null;

        if (authUserId) {
          const { data: userRow } = await supabase
            .from('users')
            .select('id')
            .eq('auth_user_id', authUserId)
            .maybeSingle();
          if (userRow?.id) {
            canonicalUserId = userRow.id;
            setCurrentUserId(userRow.id);
          }
        }

        if (!canonicalUserId) {
          throw new Error('authenticated_user_not_resolved');
        }

        const creatorId = row?.created_by || row?.creator_id || null;
        const userIsCreator = !!(canonicalUserId && creatorId && canonicalUserId === creatorId);
        if (userIsCreator) {
          alert("This is your call. You can't cast a separate jury vote.");
          return;
        }

        // ========== AUTHENTICATED USER VOTE ==========
        // Check if already voted
        const { data: existingVote } = await supabase
          .from('votes')
          .select('id')
          .eq('trial_id', id)
          .eq('user_id', canonicalUserId)
          .single();

        if (existingVote) {
          setHasVoted(true);
          alert('You already voted on this trial!');
          return;
        }

        // Record vote with user_id
        console.log('[vote_submit_attempt]', {
          trial_id: id,
          side,
          mode: 'authenticated',
          user_id: canonicalUserId,
          anon_id_present: false,
          source,
        });
        const { error: voteError } = await supabase
          .from('votes')
          .insert([{
            trial_id: id,
            user_id: canonicalUserId,
            vote_side: side,
          }]);

        if (voteError) {
          console.log('[vote_submit_result]', {
            trial_id: id,
            side,
            mode: 'authenticated',
            ok: false,
            error_code: voteError.code || null,
            error_message: voteError.message || String(voteError),
            source,
          });
          if (voteError.code === '23505') {
            setHasVoted(true);
            alert('You already voted on this trial!');
            return;
          }
          if (voteError.code === '23514' && String(voteError.message || '').includes('creator_jury_vote_forbidden')) {
            alert("This is your call. You can't cast a separate jury vote.");
            return;
          }
          throw voteError;
        }
        console.log('[vote_submit_result]', {
          trial_id: id,
          side,
          mode: 'authenticated',
          ok: true,
          error_code: null,
          error_message: null,
          source,
        });

        // 📊 Track successful vote
        if (window.trackEvent) {
          window.trackEvent('vote_success', { trial_id: id, side, is_authenticated: true });
        }

        // 🔥 Update streak for actual activity (voting)
        await updateStreakForActivity(canonicalUserId);

        try {
          await recordReferralMilestone('first_vote_completed');
        } catch (milestoneErr) {
          console.warn('⚠️ Failed to record referral first_vote_completed milestone:', milestoneErr?.message || milestoneErr);
        }

        setHasVoted(true);
        setLastVoteSide(side === 'A' ? row.side_a : row.side_b);
        const pendingCrewChallenge = consumeCrewDailyChallengePending();
        if (pendingCrewChallenge?.crew_name) {
          alert(`✅ You helped keep ${pendingCrewChallenge.crew_name} alive today.`);
          window.dispatchEvent(new Event('crew-challenge-vote-success'));
        }

      } else {
        // ========== GUEST VOTE (vote-first onboarding) ==========
        // Record vote through dedicated backend path
        console.log('[vote_submit_attempt]', {
          trial_id: id,
          side,
          mode: 'guest',
          user_id: null,
          anon_id_present: Boolean(anonId),
          source,
        });
        const {
          data: guestVoteResponse,
          error: guestVoteInvokeError,
        } = await (async () => {
          try {
            const response = await fetch(`${supabaseUrl}/functions/v1/cast-guest-trial-vote`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                apikey: supabaseAnonKey,
                Authorization: `Bearer ${supabaseAnonKey}`,
              },
              body: JSON.stringify({
                trial_id: id,
                anon_id: anonId,
                vote_side: side,
              }),
            });
            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              return {
                data: payload,
                error: {
                  code: response.status,
                  message: payload?.error || `guest_vote_http_${response.status}`,
                  reason: payload?.reason || null,
                },
              };
            }
            return { data: payload, error: null };
          } catch (invokeErr) {
            return {
              data: null,
              error: {
                code: null,
                message: invokeErr?.message || 'guest_vote_network_failed',
                reason: 'backend_failure',
              },
            };
          }
        })();

        if (guestVoteInvokeError || !guestVoteResponse?.ok) {
          const voteError = guestVoteInvokeError || {
            code: guestVoteResponse?.status || null,
            message: guestVoteResponse?.error || 'guest_vote_failed',
            reason: guestVoteResponse?.reason || 'backend_failure',
          };
          const shouldAuthRedirect = shouldRedirectGuestVoteToAuth(voteError);
          console.log('[vote_submit_result]', {
            trial_id: id,
            side,
            mode: 'guest',
            ok: false,
            error_code: voteError.code || null,
            error_message: voteError.message || String(voteError),
            error_reason: voteError.reason || guestVoteResponse?.reason || null,
            should_auth_redirect: shouldAuthRedirect,
            source,
          });
          if ((voteError.reason || guestVoteResponse?.reason) === 'already_voted' || voteError.code === '23505') {
            setHasVoted(true);
            alert('You already voted on this trial!');
            return;
          }
          if (
            shouldAuthRedirect &&
            !options?.skipGuestFallback &&
            typeof window !== 'undefined' &&
            !Capacitor.isNativePlatform()
          ) {
            try {
              localStorage.setItem(PENDING_TRIAL_VOTE_KEY, JSON.stringify({
                trialId: id,
                side,
                storedAt: Date.now(),
                returnUrl: `${window.location.pathname}${window.location.search}`,
              }));
              localStorage.setItem('gweffit_return_url', `${window.location.pathname}${window.location.search}`);
            } catch {}

            console.log('[vote_redirect_to_auth]', {
              trial_id: id,
              side,
              reason: voteError.message || String(voteError),
              source,
            });
            setLastVoteSide(side === 'A' ? row.side_a : row.side_b);
            setClaimModalMode('pre_vote');
            setShowClaimModal(true);
            return;
          }
          alert(getGuestVoteErrorMessage(voteError));
          return;
        }
        console.log('[vote_submit_result]', {
          trial_id: id,
          side,
          mode: 'guest',
          ok: true,
          error_code: null,
          error_message: null,
          source,
        });

        // 📊 Track successful vote
        if (window.trackEvent) {
          window.trackEvent('vote_success', { trial_id: id, side, is_authenticated: false });
        }

        setHasVoted(true);
        setLastVoteSide(side === 'A' ? row.side_a : row.side_b);
        const pendingCrewChallenge = consumeCrewDailyChallengePending();
        if (pendingCrewChallenge?.crew_name) {
          alert(`✅ You helped keep ${pendingCrewChallenge.crew_name} alive today.`);
          window.dispatchEvent(new Event('crew-challenge-vote-success'));
        }

        // Store return URL for after auth
        localStorage.setItem('gweffit_return_url', `${window.location.pathname}${window.location.search}`);

        // Show claim modal after a brief delay to let the vote feel confirmed
        setTimeout(() => {
          setClaimModalMode('post_vote');
          setShowClaimModal(true);
        }, 800);
      }

      const totalBefore = Number(row?.votes_a || 0) + Number(row?.votes_b || 0);
      const totalSeats = Number(row?.jury_size || window.BETA_JURY_SIZE || 6);
      const optimisticFilled = Math.min(totalSeats, Math.max(totalBefore, 0) + 1);
      setOptimisticJuryFilled((prev) => Math.max(Number(prev || 0), optimisticFilled));
      if (process.env.NODE_ENV !== 'production') {
        console.log('[jury] optimistic_after_vote', {
          trialId: id,
          filledBefore: totalBefore,
          filledAfter: optimisticFilled,
          total: totalSeats,
        });
      }

      setRow((prev) => {
        if (!prev) return prev;
        const nextVotesA = Number(prev.votes_a || 0) + (side === 'A' ? 1 : 0);
        const nextVotesB = Number(prev.votes_b || 0) + (side === 'B' ? 1 : 0);
        const nextTotal = Number(prev.total_votes ?? (Number(prev.votes_a || 0) + Number(prev.votes_b || 0))) + 1;
        return { ...prev, votes_a: nextVotesA, votes_b: nextVotesB, total_votes: nextTotal };
      });

      // ✅ Re-fetch counts after vote (DB trigger already updated trials)
      const { data: updatedTrial, error: updatedTrialError } = await supabase
        .from('trials')
        .select('votes_a, votes_b, total_votes')
        .eq('id', id)
        .single();

      if (!updatedTrialError && updatedTrial) {
        setRow(prev => (prev ? { ...prev, ...updatedTrial } : prev));
        const refreshedTotal = Number(updatedTrial.votes_a || 0) + Number(updatedTrial.votes_b || 0);
        setOptimisticJuryFilled((prev) => {
          if (prev == null) return prev;
          return refreshedTotal >= prev ? null : prev;
        });

        const statusNorm = String(row?.status || '').toLowerCase();
        const isTerminal = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(statusNorm);
        const jurySize = Number(row?.jury_size || window.BETA_JURY_SIZE || 6);
        if (currentUserId && !isTerminal && refreshedTotal >= jurySize) {
          try {
            await supabase.functions.invoke('finalize-trial-status', {
              body: { trial_id: id, reason: 'jury_full_vote' },
            });
          } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('[trial_detail] finalize_failed', { trial_id: id, error: err?.message || String(err) });
            }
          }
        }
      }

    } catch (e) {
      console.error(e);
      console.log('[vote_submit_result]', {
        trial_id: id,
        side,
        mode: isAuthenticated ? 'authenticated' : 'guest',
        ok: false,
        error_code: e?.code || null,
        error_message: e?.message || String(e),
        source,
      });
      alert('Vote failed. Please try again.');
    } finally {
      setVoting(false);
    }
  }

  useEffect(() => {
    let alive = true;
    let timeoutId = null;
    let settled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      console.log('[trial] load_start', { trial_id: id });
      timeoutId = setTimeout(() => {
        if (!alive || settled) return;
        console.warn('[trial] load_timeout', { trial_id: id, timeout_ms: 10000 });
        setErr('Loading timed out. Please retry.');
        setLoading(false);
      }, 10000);

      // 📊 Track trial detail viewed (funnel: open -> vote -> verdict)
      try {
        if (window.trackEvent) {
          window.trackEvent('trial_detail_viewed', {
            trial_id: id,
            referrer: document.referrer || 'direct',
          });
        }
      } catch {}

      // Fetch trial data and canonical verdict cache snapshot used by Vault.
      const [trialRes, cacheRes] = await Promise.all([
        supabase
          .from('trials')
          .select(
            'id,title,side_a,side_b,image_emoji,trial_type,status,votes_a,votes_b,total_votes,created_at,expires_at,closed_at,original_trial_id,creator_id,created_by,trial_mode,decision_category,jury_size,time_limit_seconds,is_seeded,seed_source,verdict_data'
          )
          .eq('id', id)
          .maybeSingle(),
        supabase
          .from('verdict_cache')
          .select('trial_id,status,generated_at,image_url,roast_punchline,sports_punchline,internet_punchline,verdict_type,winner,winner_pct')
          .eq('trial_id', id)
          .eq('status', 'completed')
          .maybeSingle(),
      ]);
      const { data, error } = trialRes;

      if (!alive) return;

      if (error) {
        settled = true;
        if (timeoutId) clearTimeout(timeoutId);
        console.error('[trial] load_error', { trial_id: id, error: error.message });
        setErr(error.message);
        setLoading(false);
        return;
      }
      if (!data) {
        settled = true;
        if (timeoutId) clearTimeout(timeoutId);
        console.warn('[trial] load_null', { trial_id: id });
        setErr('Trial not found.');
        setRow(null);
        setLoading(false);
        return;
      }

      settled = true;
      setErr(null);
      setRow(data);
      setVerdictCacheRow(cacheRes?.data || null);
      if (timeoutId) clearTimeout(timeoutId);
      setLoading(false);
      console.log('[trial] load_success', {
        trial_id: id,
        status: data.status || null,
        cache_image_url: cacheRes?.data?.image_url || null,
        cache_caption: cacheRes?.data?.internet_punchline || cacheRes?.data?.roast_punchline || cacheRes?.data?.sports_punchline || null,
      });

      // Optional secondary fetches - never block primary render.
      const isSeededStarterTrial = Boolean(
        data.is_seeded
        || data.seed_source
        || data?.verdict_data?.seeded
      );
      setCreator(null);
      const creatorId = data.created_by || data.creator_id;
      if (creatorId && !isSeededStarterTrial) {
        supabase
          .from('users')
          .select('id,username,followers_count,following_count')
          .eq('id', creatorId)
          .single()
          .then(({ data: creatorData }) => {
            if (creatorData && alive) setCreator(creatorData);
          })
          .catch(() => {});
      }

    })();

    return () => {
      alive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [id, loadNonce]);

  // Realtime updates for live jury fill / vote counts while active
  useEffect(() => {
    if (!id) return undefined;
    const channel = supabase
      .channel(`trial-detail-${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'trials', filter: `id=eq.${id}` },
        (payload) => {
          const t = payload?.new || null;
          if (!t?.id) return;
          setRow((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              votes_a: t.votes_a ?? prev.votes_a,
              votes_b: t.votes_b ?? prev.votes_b,
              total_votes: t.total_votes ?? prev.total_votes,
              status: t.status ?? prev.status,
              expires_at: t.expires_at ?? prev.expires_at,
              closed_at: t.closed_at ?? prev.closed_at,
              verdict_data: t.verdict_data ?? prev.verdict_data,
            };
          });
          const refreshedTotal = Number(t.votes_a || 0) + Number(t.votes_b || 0);
          setOptimisticJuryFilled((prev) => {
            if (prev == null) return prev;
            return refreshedTotal >= prev ? null : prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setHasVoted(false);

    if (currentUserId) {
      supabase
        .from('votes')
        .select('id')
        .eq('trial_id', id)
        .eq('user_id', currentUserId)
        .single()
        .then(({ data: voteRow }) => {
          if (voteRow && alive) setHasVoted(true);
        })
        .catch(() => {});
      return () => { alive = false; };
    }

    const existingAnonId = getExistingAnonId();
    if (existingAnonId) {
      supabase
        .from('votes')
        .select('id')
        .eq('trial_id', id)
        .eq('anon_id', existingAnonId)
        .single()
        .then(({ data: anonVoteRow }) => {
          if (anonVoteRow && alive) setHasVoted(true);
        })
        .catch(() => {});
    }

    return () => { alive = false; };
  }, [id, currentUserId]);

  useEffect(() => {
    let alive = true;
    const creatorId = row?.created_by || row?.creator_id || null;
    const userIsCreator = !!(currentUserId && creatorId && currentUserId === creatorId);

    if (!row?.id || !userIsCreator) {
      setCreatorCallSide(null);
      return () => { alive = false; };
    }

    supabase
      .from('trial_creator_calls')
      .select('creator_side')
      .eq('trial_id', row.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        const nextSide = String(data?.creator_side || '').toUpperCase();
        setCreatorCallSide(nextSide === 'A' || nextSide === 'B' ? nextSide : null);
      })
      .catch(() => {
        if (!alive) return;
        setCreatorCallSide(null);
      });

    return () => { alive = false; };
  }, [row?.id, row?.created_by, row?.creator_id, currentUserId]);

  const crewSignalTrialId = row?.id || null;
  const crewSignalSideA = row?.side_a || null;
  const crewSignalSideB = row?.side_b || null;
  const crewSignalVotesA = row?.votes_a || 0;
  const crewSignalVotesB = row?.votes_b || 0;
  const crewSignalTotalVotes = row?.total_votes || 0;

  useEffect(() => {
    let alive = true;
    const trialSnapshot = crewSignalTrialId
      ? {
          id: crewSignalTrialId,
          side_a: crewSignalSideA,
          side_b: crewSignalSideB,
          votes_a: crewSignalVotesA,
          votes_b: crewSignalVotesB,
          total_votes: crewSignalTotalVotes,
        }
      : null;

    const loadCrewSignals = async () => {
      if (!trialSnapshot?.id || !currentUserId) {
        if (alive) setCrewSignals([]);
        return;
      }

      try {
        const { data: membershipRows, error: membershipErr } = await supabase
          .from('crew_members')
          .select('crew_id, crews(id,name,emoji)')
          .eq('user_id', currentUserId);
        if (membershipErr) throw membershipErr;

        const crews = (membershipRows || [])
          .map((row) => row?.crews)
          .filter((crew) => crew?.id);
        const crewIds = [...new Set(crews.map((crew) => crew.id))];
        if (crewIds.length === 0) {
          if (alive) setCrewSignals([]);
          return;
        }

        const { data: crewMemberRows, error: memberErr } = await supabase
          .from('crew_members')
          .select('crew_id,user_id')
          .in('crew_id', crewIds);
        if (memberErr) throw memberErr;

        const memberIds = [...new Set((crewMemberRows || []).map((member) => member?.user_id).filter(Boolean))];
        if (memberIds.length === 0) {
          if (alive) setCrewSignals([]);
          return;
        }

        const { data: voteRows, error: voteErr } = await supabase
          .from('votes')
          .select('user_id,vote_side,created_at')
          .eq('trial_id', trialSnapshot.id)
          .in('user_id', memberIds);
        if (voteErr) throw voteErr;

        const nextSignals = buildCrewTrialSignals(trialSnapshot, crews, crewMemberRows || [], voteRows || [], currentUserId);
        if (alive) setCrewSignals(nextSignals);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[trial_detail] crew_signals_failed', { trial_id: trialSnapshot?.id || null, error: e?.message || String(e) });
        }
        if (alive) setCrewSignals([]);
      }
    };

    loadCrewSignals();
    return () => { alive = false; };
  }, [crewSignalTrialId, crewSignalVotesA, crewSignalVotesB, crewSignalTotalVotes, currentUserId, row?.status, crewSignalSideA, crewSignalSideB]);

  useEffect(() => {
    if (!row?.id) return;
    if (!['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed', 'closed_pending_verdict', 'sudden_death_closed_pending'].includes(String(row.status || '').toLowerCase())) return;
    if (hasAuthoritativeVerdict(row)) return;

    const startedAt = Date.now();
    let alive = true;
    const interval = setInterval(async () => {
      if (!alive) return;
      if (Date.now() - startedAt > 20000) {
        clearInterval(interval);
        return;
      }
      const { data: refreshed } = await supabase
        .from('trials')
        .select('id,status,votes_a,votes_b,total_votes,closed_at,verdict_data')
        .eq('id', row.id)
        .maybeSingle();
      if (!alive || !refreshed?.id) return;
      setRow((prev) => (prev?.id === refreshed.id ? { ...prev, ...refreshed } : prev));
      if (hasAuthoritativeVerdict(refreshed)) {
        clearInterval(interval);
      }
    }, 2500);

    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [row, row?.id, row?.status, row?.verdict_data, hasAuthoritativeVerdict]);

  useEffect(() => {
    if (!row?.id) return;
    const isClosedStatus = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed', 'closed_pending_verdict', 'sudden_death_closed_pending'].includes(String(row.status || '').toLowerCase());
    if (!isClosedStatus) return;
    if (verdictCacheAppliedRef.current.has(row.id)) return;
    if (verdictCacheInFlightRef.current.has(row.id)) return;
    const hasImage = Boolean(getVerdictImageUrl(row));
    const hasLine = Boolean(row?.verdict_data?.verdict_line) || (!isGenericFallbackPunchline(row?.verdict_data?.punchline) ? Boolean(row?.verdict_data?.punchline) : false);
    const imageSource = String(row?.verdict_data?.image_source || '').toLowerCase();
    const isAiSource = String(row?.verdict_data?.verdict_source || '').toLowerCase() === 'ai';
    if (hasImage && hasLine && (isAiSource || imageSource === 'ai_primary')) {
      verdictCacheAppliedRef.current.add(row.id);
      return;
    }
    verdictCacheInFlightRef.current.add(row.id);

    let alive = true;
    (async () => {
      try {
        // Cache is canonical when present; sync it into verdict_data.
        const { data: cacheRow } = await supabase
          .from('verdict_cache')
          .select('image_url,roast_punchline,sports_punchline,internet_punchline,status')
          .eq('trial_id', row.id)
          .eq('status', 'completed')
          .maybeSingle();
        if (!alive) return;
        if (!cacheRow) {
          verdictCacheInFlightRef.current.delete(row.id);
          return;
        }
        setVerdictCacheRow(cacheRow);
        const latestVerdict = (rowRef.current?.verdict_data && typeof rowRef.current.verdict_data === 'object')
          ? rowRef.current.verdict_data
          : {};
        if (String(latestVerdict.image_source || '').toLowerCase() === 'ai_primary') {
          verdictCacheAppliedRef.current.add(row.id);
          return;
        }
        const cacheLine = cacheRow.internet_punchline || cacheRow.roast_punchline || cacheRow.sports_punchline || null;
        const mergedVerdict = {
          ...latestVerdict,
          image_url: cacheRow.image_url ?? null,
          ai_image: cacheRow.image_url ?? null,
          aiImage: cacheRow.image_url ?? null,
          verdict_line: cacheLine ?? null,
          verdict_source: 'ai',
        };
        await supabase
          .from('trials')
          .update({ verdict_data: mergedVerdict })
          .eq('id', row.id);
        setRow((prev) => (prev?.id === row.id ? { ...prev, verdict_data: mergedVerdict } : prev));
        verdictCacheAppliedRef.current.add(row.id);
      } catch (err) {
        console.warn('[verdict] cache_hydrate_failed', { trial_id: row.id, reason: err?.message || String(err) });
      } finally {
        verdictCacheInFlightRef.current.delete(row.id);
      }
    })();

    return () => { alive = false; };
  }, [row, getVerdictImageUrl, isGenericFallbackPunchline]);

  useEffect(() => {
    if (!row?.id) return undefined;
    const isClosedStatus = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed', 'closed_pending_verdict', 'sudden_death_closed_pending'].includes(String(row.status || '').toLowerCase());
    const imageUrl = premiumMemeImageOverride || getVerdictImageUrl(row);
    const nonGenericLine = !isGenericFallbackPunchline(row?.verdict_data?.punchline)
      ? row?.verdict_data?.punchline
      : null;
    const renderLine = row?.verdict_data?.verdict_line || nonGenericLine || row?.verdict_data?.snark || null;
    const isPendingVerdict = !imageUrl && !renderLine;
    if (!isClosedStatus || !isPendingVerdict) return undefined;
    let alive = true;
    const interval = setInterval(async () => {
      try {
        const { data: cacheRow } = await supabase
          .from('verdict_cache')
          .select('image_url,roast_punchline,sports_punchline,internet_punchline,status')
          .eq('trial_id', row.id)
          .eq('status', 'completed')
          .maybeSingle();
        if (!alive || !cacheRow?.image_url) return;
        setVerdictCacheRow(cacheRow);
        const latestVerdict = (rowRef.current?.verdict_data && typeof rowRef.current.verdict_data === 'object')
          ? rowRef.current.verdict_data
          : {};
        if (String(latestVerdict.image_source || '').toLowerCase() === 'ai_primary') {
          clearInterval(interval);
          return;
        }
        const cacheLine =
          cacheRow.internet_punchline ||
          cacheRow.roast_punchline ||
          cacheRow.sports_punchline ||
          latestVerdict.verdict_line ||
          latestVerdict.punchline ||
          null;
        const mergedVerdict = {
          ...latestVerdict,
          image_url: cacheRow.image_url ?? null,
          ai_image: cacheRow.image_url ?? null,
          aiImage: cacheRow.image_url ?? null,
          share_card_url: latestVerdict.share_card_url || cacheRow.image_url || null,
          verdict_line: cacheLine ?? null,
          verdict_source: latestVerdict.verdict_source || 'ai',
        };
        await supabase
          .from('trials')
          .update({ verdict_data: mergedVerdict })
          .eq('id', row.id);
        if (!alive) return;
        setRow((prev) => (prev?.id === row.id ? { ...prev, verdict_data: mergedVerdict } : prev));
        verdictCacheAppliedRef.current.add(row.id);
        clearInterval(interval);
      } catch (err) {
        console.warn('[verdict] pending_cache_poll_failed', { trial_id: row.id, reason: err?.message || String(err) });
      }
    }, 5000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [row, premiumMemeImageOverride, getVerdictImageUrl, isGenericFallbackPunchline]);

  async function getReferralShareUrl() {
    let inviterUserId = null;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const authUserId = sessionData?.session?.user?.id || null;
      if (authUserId) {
        const { data: userRow } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        inviterUserId = userRow?.id || null;
      }
    } catch {}

    return getShareUrl('trial', id, inviterUserId ? {
      ref: inviterUserId,
      ref_src: 'share_link',
      ref_id: id,
    } : {});
  }

  async function handleCopyLink() {
    try {
      const shareUrl = await getReferralShareUrl();
      await copyTextReliably(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn('[share] copy_link_failed', {
        trialId: id,
        error: err?.message || String(err),
      });
      showSurfaceNotice('Copy failed', 'error');
    }
  }

  async function blobToBase64(blob) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        if (!base64) {
          reject(new Error('base64_encode_failed'));
          return;
        }
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error || new Error('file_reader_failed'));
      reader.readAsDataURL(blob);
    });
  }

  async function persistArtifactForNative(artifact) {
    if (!artifact?.blob) return null;
    const base64Data = await blobToBase64(artifact.blob);
    const extension = artifact.extension || 'png';
    const fileName = `gweffit-verdict-${id}-${Date.now()}.${extension}`;
    const writeResult = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });
    return {
      ...artifact,
      fileName,
      uri: writeResult?.uri || null,
    };
  }

  async function buildVerdictArtifactBlob() {
    if (verdictArtifactImageUrl) {
      try {
        const response = await fetch(verdictArtifactImageUrl);
        if (response.ok) {
          const blob = await response.blob();
          const mimeType = String(blob?.type || '').toLowerCase();
          const isImage = mimeType.startsWith('image/');
          const isRaster = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(mimeType);
          if (blob && blob.size > 0 && isImage && isRaster) {
            return {
              blob,
              mimeType,
              extension: getDownloadExtensionForMimeType(mimeType),
              source: 'remote_asset',
            };
          }
        }
      } catch (err) {
        console.warn('[share] verdict_asset_fetch_failed', {
          trialId: id,
          error: err?.message || String(err),
        });
      }
    }

    const shareCardNode = document.getElementById('trial-detail-verdict-share-card');
    if (!shareCardNode) return null;

    try {
      const canvas = await html2canvas(shareCardNode, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      return await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob ? {
          blob,
          mimeType: 'image/png',
          extension: 'png',
          source: 'canvas_capture',
        } : null), 'image/png');
      });
    } catch (err) {
      console.warn('[share] verdict_artifact_capture_failed', {
        trialId: id,
        error: err?.message || String(err),
      });
      return null;
    }
  }

  async function handleShareTrial() {
    const shareUrl = await getReferralShareUrl();
    const isClosedVerdictShare = Boolean(isClosed && (verdictImageUrl || verdictLine));
    const shareTitle = isClosedVerdictShare
      ? `${row?.title || 'GweffIT Verdict'}`
      : (row?.title || 'GweffIT Trial');
    const shareText = isClosedVerdictShare
      ? `The verdict is in: ${row?.title || 'GweffIT Verdict'}`
      : (row?.title ? `Vote on: ${row.title}` : 'Vote on this GweffIT trial');
    let methodUsed = 'clipboard';
    try {
      if (isClosedVerdictShare) {
        const artifactBlob = await buildVerdictArtifactBlob();
        const isNative = Capacitor.isNativePlatform?.();

        if (isNative && artifactBlob?.blob) {
          const persistedArtifact = await persistArtifactForNative(artifactBlob);
          if (!persistedArtifact?.uri) throw new Error('native_file_persist_failed');
          methodUsed = 'capacitor.share.file';
          await Share.share({
            title: shareTitle,
            url: persistedArtifact.uri,
            dialogTitle: 'Send Verdict',
          });
        } else {
          const verdictFile = artifactBlob?.blob
            ? new File(
                [artifactBlob.blob],
                `gweffit-verdict-${id}.${artifactBlob.extension || 'png'}`,
                { type: artifactBlob.mimeType || 'image/png' }
              )
            : null;

          if (
            verdictFile
            && typeof navigator !== 'undefined'
            && typeof navigator.share === 'function'
            && typeof navigator.canShare === 'function'
            && navigator.canShare({ files: [verdictFile] })
          ) {
            methodUsed = 'navigator.share.file';
            await navigator.share({
              title: shareTitle,
              files: [verdictFile],
            });
          } else if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
            methodUsed = 'navigator.share';
            await navigator.share({
              title: shareTitle,
              text: verdictLine || shareText,
              url: shareUrl,
            });
          } else {
            await copyTextReliably(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            showSurfaceNotice('Verdict link copied.');
          }
        }
      } else if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        methodUsed = 'navigator.share';
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } else {
        await copyTextReliably(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        showSurfaceNotice(isClosedVerdictShare ? 'Verdict link copied.' : 'Link copied.');
      }
      if (process.env.NODE_ENV !== 'production') {
        console.log('[share] trial_share_attempt', {
          trialId: id,
          hasShareUrl: false,
          platform: window?.Capacitor?.getPlatform?.() || 'web',
          methodUsed,
          ok: true,
          error: null,
        });
      }
    } catch (err) {
      try {
        await copyTextReliably(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        showSurfaceNotice(isClosedVerdictShare ? 'Verdict link copied.' : 'Link copied.');
        if (process.env.NODE_ENV !== 'production') {
          console.log('[share] trial_share_attempt', {
            trialId: id,
            hasShareUrl: false,
            platform: window?.Capacitor?.getPlatform?.() || 'web',
            methodUsed: 'clipboard_fallback',
            ok: true,
            error: err?.message || String(err),
          });
        }
      } catch (fallbackErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[share] trial_share_attempt', {
            trialId: id,
            hasShareUrl: false,
            platform: window?.Capacitor?.getPlatform?.() || 'web',
            methodUsed,
            ok: false,
            error: fallbackErr?.message || String(fallbackErr),
          });
        }
        showSurfaceNotice('Share failed', 'error');
      }
    }
  }

  async function handleDownloadCard() {
    if (isClosed) {
      const artifactBlob = await buildVerdictArtifactBlob();
      if (artifactBlob?.blob) {
        try {
          if (Capacitor.isNativePlatform?.()) {
            const persistedArtifact = await persistArtifactForNative(artifactBlob);
            if (!persistedArtifact?.uri) throw new Error('native_file_persist_failed');
            await Share.share({
              title: 'Save Verdict',
              text: row?.title || 'GweffIT Verdict',
              url: persistedArtifact.uri,
              dialogTitle: 'Save Verdict',
            });
            return;
          }
          const blobUrl = URL.createObjectURL(artifactBlob.blob);
          const link = document.createElement('a');
          link.download = `gweffit-verdict-${id}.${artifactBlob.extension || 'png'}`;
          link.href = blobUrl;
          link.click();
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          return;
        } catch (e) {
          console.warn('[download] verdict_blob_download_failed', {
            trialId: id,
            error: e?.message || String(e),
          });
        }
      }
    }

    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: null, scale: 2 });
      const link = document.createElement('a');
      link.download = `gweffit-verdict-${id}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error(e);
      showSurfaceNotice('Download failed', 'error');
    }
  }

  async function handleReopenTieBreaker() {
    if (!row?.id || reopeningTieBreaker) return;

    setReopeningTieBreaker(true);
    try {
      const { data, error } = await supabase.rpc('reopen_trial_tiebreaker', {
        p_trial_id: row.id,
        p_window_minutes: 10,
      });

      if (error) throw error;
      const reopened = Array.isArray(data) ? data[0] : data;
      if (!reopened?.ok) throw new Error('Failed to reopen tie-breaker');

      if (window.trackEvent) {
        window.trackEvent('trial_tiebreaker_reopened', {
          trial_id: row.id,
          mode: 'next_vote_or_timeout',
          window_minutes: 10,
        });
      }

      setLoadNonce((v) => v + 1);
    } catch (e) {
      console.error('[trial] reopen_tiebreaker_failed', {
        trial_id: row?.id,
        message: e?.message || String(e),
      });
      showSurfaceNotice(`Could not reopen tie-breaker: ${e?.message || 'unknown_error'}`, 'error');
    } finally {
      setReopeningTieBreaker(false);
      setShowTieBreakerPrompt(false);
    }
  }

  async function handleSubmitReport() {
    if (!currentUserId) {
      alert('Please sign in to report.');
      return;
    }
    setReportSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('submit_report', {
        p_kind: 'trial',
        p_target_id: row.id,
        p_reason: reportReason,
        p_note: reportNote.trim() || null,
      });
      if (error) throw error;
      if (!data?.ok) {
        if (data?.error === 'report_duplicate_24h') {
          showSurfaceNotice('Already reported. Thanks.');
          setShowReportModal(false);
          setReportNote('');
          return;
        }
        if (data?.error === 'rate_limited') {
          throw new Error('Report limit reached (5 per 24 hours). Try again later.');
        }
        if (data?.error === 'report_rate_limited') {
          throw new Error('You’ve hit the report limit (5/day).');
        }
        throw new Error(data?.error || 'report_submit_failed');
      }
      setShowReportModal(false);
      hideTrialLocally(row.id);
      setReportNote('');
      setReportReason('spam');
      showSurfaceNotice('Reported and hidden from your feed.');
    } catch (err) {
      console.error('[trial] report_failed', {
        trial_id: row?.id,
        message: err?.message || String(err),
      });
      showSurfaceNotice(`Could not submit report: ${err?.message || 'unknown_error'}`, 'error');
    } finally {
      setReportSubmitting(false);
    }
  }

  const statusLower = String(row?.status || '').toLowerCase();
  const isVerdictLocking = ['closed_pending_verdict', 'sudden_death_closed_pending'].includes(statusLower);
  const isRematch = !!row?.title?.includes('(Rematch)');
  const isVerdictResolved = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(statusLower);
  const isClosed = isVerdictLocking || isVerdictResolved;
  const isSuddenDeathOpen = statusLower === 'sudden_death_open';
  const suddenDeathSummary = row?.verdict_data?.sudden_death_summary || null;
  const resolvedInSuddenDeath = Boolean(row?.verdict_data?.is_sudden_death);
  const verdictSource = String(row?.verdict_data?.verdict_source || '').toLowerCase();
  const verdictReasonCode = row?.verdict_data?.reason_code || null;
  const resolvedVerdictAssetSource = row ? { ...row, ...(verdictCacheRow || {}) } : row;
  const resolvedVerdictRender = deriveTrialVerdictRenderData(resolvedVerdictAssetSource);
  const resolvedCanonicalVerdictAsset = getCanonicalTrialDetailVerdictUrl(resolvedVerdictAssetSource);
  const rowCanonicalVerdictAsset = getCanonicalTrialDetailVerdictUrl(row);
  const canUseResolvedCanonicalAsset = Boolean(
    resolvedCanonicalVerdictAsset.url &&
    resolvedCanonicalVerdictAsset.source &&
    resolvedCanonicalVerdictAsset.source !== 'derived_bucket_svg'
  );
  const canUseRowCanonicalAsset = Boolean(
    rowCanonicalVerdictAsset.url &&
    rowCanonicalVerdictAsset.source &&
    rowCanonicalVerdictAsset.source !== 'derived_bucket_svg'
  );
  const verdictImageUrl =
    premiumMemeImageOverride ||
    (canUseResolvedCanonicalAsset ? resolvedCanonicalVerdictAsset.url : null) ||
    (canUseRowCanonicalAsset ? rowCanonicalVerdictAsset.url : null) ||
    getVisibleVerdictImageUrl(resolvedVerdictAssetSource) ||
    getVisibleVerdictImageUrl(row) ||
    resolvedCanonicalVerdictAsset.url ||
    rowCanonicalVerdictAsset.url;
  const verdictArtifactImageUrl =
    premiumMemeImageOverride ||
    resolvedCanonicalVerdictAsset.url ||
    rowCanonicalVerdictAsset.url ||
    getVerdictImageUrl(resolvedVerdictAssetSource) ||
    getVerdictImageUrl(row);
  const hasPremiumMeme = String(
    resolvedCanonicalVerdictAsset.imageSource ||
    rowCanonicalVerdictAsset.imageSource ||
    row?.verdict_data?.image_source ||
    ''
  ).toLowerCase() === 'ai_primary' && Boolean(
    resolvedCanonicalVerdictAsset.url ||
    rowCanonicalVerdictAsset.url ||
    premiumMemeImageOverride
  );
  const suppressPremiumMemeLocalOverlay = Boolean(
    hasPremiumMeme && Boolean(verdictImageUrl)
  );
  const nonGenericPunchline = !isGenericFallbackPunchline(row?.verdict_data?.punchline)
    ? row?.verdict_data?.punchline
    : null;
  const verdictLine = resolvedVerdictRender.captionFull || row?.verdict_data?.verdict_line || nonGenericPunchline || row?.verdict_data?.snark || null;
  useEffect(() => {
    if (!row?.id) return;
    console.log('[verdict_detail_asset_selected]', {
      trial_id: row.id,
      selected_image_url: verdictImageUrl || null,
      selected_image_source: verdictImageUrl ? 'detail_display_asset' : null,
      artifact_image_url: verdictArtifactImageUrl || null,
      artifact_image_source: resolvedCanonicalVerdictAsset.source || rowCanonicalVerdictAsset.source || null,
      image_source: row?.verdict_data?.image_source || null,
      cache_image_url: verdictCacheRow?.image_url || null,
      share_card_url: row?.verdict_data?.share_card_url || null,
      composed_share_card_url: row?.verdict_data?.composed_share_card_url || null,
      background_image_url: row?.verdict_data?.background_image_url || null,
      verdict_line: verdictLine || null,
    });
  }, [row?.id, row?.verdict_data, verdictImageUrl, verdictArtifactImageUrl, verdictCacheRow, verdictLine, resolvedCanonicalVerdictAsset, rowCanonicalVerdictAsset]);
  // Closed-trial render rules:
  // 1) verdict_cache exists -> use cache (and sync into verdict_data)
  // 2) cache absent + verdict_data exists -> use verdict_data fallback
  // 3) neither exists -> show pending
  const verdictRenderState = isClosed
    ? (verdictImageUrl && verdictLine
      ? 'image_and_line'
      : verdictImageUrl
        ? 'image_only'
        : verdictLine
          ? 'line_only'
          : 'pending')
    : 'not_closed';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center px-6 pb-6" style={pageShellStyle}>
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading trial...
      </div>
    );
  }

  if (err || !row) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 px-6 pb-6" style={pageShellStyle}>
        <div className="max-w-3xl mx-auto">
          <div className="p-6 rounded-2xl border border-slate-700 bg-slate-800/80">
            <div className="font-extrabold text-xl">Couldn&apos;t load that trial.</div>
            {err && <pre className="mt-2 text-xs text-slate-400">{err}</pre>}
            <button
              onClick={() => {
                setErr(null);
                setLoadNonce((v) => v + 1);
              }}
              className="mt-4 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (row?.is_blocked || (row?.is_quarantined && !canModerationView)) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 px-6 pb-6" style={pageShellStyle}>
        <div className="max-w-3xl mx-auto">
          <div className="p-6 rounded-2xl border border-slate-700 bg-slate-800/80">
            <div className="font-extrabold text-xl">{row?.is_quarantined ? 'This trial is under review.' : 'This trial is unavailable.'}</div>
            <div className="mt-2 text-sm text-slate-300">
              {row?.is_quarantined
                ? 'This trial is temporarily hidden while we review reports.'
                : 'It may have been removed for safety or moderation reasons.'}
            </div>
            <Link
              to="/"
              className="mt-4 inline-flex rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }
  const showPendingVerdictBanner = isClosed
    && !hasAuthoritativeVerdict(row)
    && (
      isVerdictLocking
      || !row?.verdict_data?.punchline
      || ['awaiting_ai_generation', 'awaiting_ai'].includes(String(verdictReasonCode || '').toLowerCase())
    );
  const isVerdictReadyForDisplay = isVerdictResolved && verdictRenderState !== 'pending' && !showPendingVerdictBanner;

  const trialMode = row.trial_mode || 'debate';
  const isDecideForMe = trialMode === 'decide_for_me';

  // Check if trial was just created (within last 60 seconds)
  const isNew = row.created_at && (Date.now() - new Date(row.created_at).getTime()) < 60000;

  const votesA = Number(row.votes_a || 0);
  const votesB = Number(row.votes_b || 0);
  const total = votesA + votesB;

  const jurySize = Number(row.jury_size || window.BETA_JURY_SIZE || 6);
  const displayedFilled = Math.min(
    jurySize,
    Math.max(total, Number(optimisticJuryFilled || 0), hasVoted ? 1 : 0)
  );
  const votesRemaining = jurySize ? Math.max(0, jurySize - displayedFilled) : null;
  const progressPct = jurySize ? Math.min(100, Math.round((displayedFilled / jurySize) * 100)) : 0;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[jury] progress_calc', {
      trialId: row?.id || null,
      filled: displayedFilled,
      total: jurySize,
      userHasVoted: hasVoted,
      source: 'trial_detail_row_counts',
    });
  }

  // Stakes signals
  const isNearTie = !isClosed && total >= 4 && Math.abs(votesA - votesB) <= 1;

  // Only compute winner/percentages for closed trials
  const pctA = isClosed && total ? Math.round((votesA / total) * 100) : 0;
  const pctB = isClosed && total ? 100 - pctA : 0;
  const winner =
    isClosed
      ? votesA === votesB
        ? 'Tie'
        : votesA > votesB
          ? row.side_a
          : row.side_b
      : null;

  const creatorId = row.created_by || row.creator_id;
  const isSeededStarterTrial = Boolean(
    row.is_seeded
    || row.seed_source
    || row?.verdict_data?.seeded
  );
  const isCreator = !!(currentUserId && creatorId && currentUserId === creatorId);
  const creatorCallLabel = creatorCallSide === 'A' ? row.side_a : creatorCallSide === 'B' ? row.side_b : null;
  const creatorCallOutcome = isClosed && creatorCallSide && winner
    ? (winner === 'Tie' ? 'push' : (creatorCallLabel === winner ? 'won' : 'lost'))
    : null;

  const comebackRole = comebackRoleParam === 'creator' || comebackRoleParam === 'voter'
    ? comebackRoleParam
    : (isClosed && isCreator ? 'creator' : (isClosed && hasVoted ? 'voter' : ''));
  const isVerdictComeback = isClosed && (
    notificationTypeParam === 'verdict_ready'
    || comebackSource === 'what_changed'
    || comebackRole === 'creator'
    || comebackRole === 'voter'
  );
  const comebackEyebrow = comebackRole === 'creator'
    ? 'Your Receipt Is Ready'
    : comebackRole === 'voter'
      ? 'Verdict Landed'
      : 'Verdict Landed';
  const comebackHeading = comebackRole === 'creator'
    ? (
      creatorCallOutcome === 'won'
        ? 'The jury backed you.'
        : creatorCallOutcome === 'lost'
          ? 'The jury ruled against you.'
          : (creatorCallLabel
            ? `Your call on ${creatorCallLabel} landed.`
            : 'Your call landed.')
    )
    : comebackRole === 'voter'
      ? 'The verdict you backed is in.'
      : 'Here’s how it ended.';
  const comebackBody = comebackRole === 'creator'
    ? (
      creatorCallOutcome === 'won'
        ? 'See the verdict below — then brag with a meme.'
        : creatorCallOutcome === 'lost'
          ? 'See the verdict below — then meme the fallout.'
          : (creatorCallLabel
            ? `You backed ${creatorCallLabel} before the jury ruled. This one is resolved. Send the receipt, or make the louder version below.`
            : 'This one is resolved. Send the receipt, or make the louder version below.')
    )
    : comebackRole === 'voter'
      ? 'You came back for the result. Open the outcome and see how it landed.'
      : 'The result is ready.';
  const sendBlockTitle = comebackRole === 'creator'
    ? (hasPremiumMeme ? 'Send your receipt' : 'Your receipt is ready to send')
    : comebackRole === 'voter'
      ? (hasPremiumMeme ? 'Send the verdict' : 'The verdict is ready to send')
      : (hasPremiumMeme ? 'Send the verdict' : 'The verdict is ready to send');
  const sendBlockBody = comebackRole === 'creator'
    ? (hasPremiumMeme
      ? (creatorCallLabel ? `You backed ${creatorCallLabel}. Put the receipt in the group chat.` : 'Your call landed. Put the receipt in the group chat.')
      : (creatorCallLabel ? `You backed ${creatorCallLabel}. Send the receipt now, or make the louder version below.` : 'Your call landed. Send the receipt now, or make the louder version below.'))
    : comebackRole === 'voter'
      ? (hasPremiumMeme
        ? 'The result is in. Send it if you want.'
        : 'The result is in. Share it if you want, or just keep the receipt.')
      : (hasPremiumMeme
        ? 'You have the louder version ready. Put the result in the group chat.'
        : 'This is the receipt. Send the result now, or make the louder version below.');

  // Creator call is locked at creation time, so creators never cast separate jury votes.
  const canVote = !isCreator;

  // Build trial object for JurySummonsButton
  const trial = {
    id: row.id,
    title: row.title,
    sideA: row.side_a,
    sideB: row.side_b,
    image: row.image_emoji || '⚖️',
    status: row.status,
    trialMode: trialMode,
    decisionCategory: row.decision_category || null,
  };

  // Build user object for JurySummonsButton (minimal)
  const user = currentUserId ? { id: currentUserId } : null;
  const visibleCrewSignals = crewSignals.slice(0, 2);
  const primaryCrewSignal = visibleCrewSignals[0] || null;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 px-6 pb-6" style={pageShellStyle}>
      {/* Open in App Banner - mobile web only */}
      <OpenInAppBanner type="trial" id={id} title={row?.title} />
      {surfaceNotice?.message ? (
        <div className="fixed inset-x-0 top-3 z-[2200] px-4">
          <div
            className={`mx-auto max-w-md rounded-xl border px-4 py-3 text-sm font-semibold shadow-2xl ${
              surfaceNotice.tone === 'error'
                ? 'border-red-500/60 bg-red-950/95 text-red-100'
                : 'border-emerald-500/50 bg-emerald-950/95 text-emerald-100'
            }`}
          >
            {surfaceNotice.message}
          </div>
        </div>
      ) : null}
      {!currentUserId && showColdTrafficBanner && (
        <div className="max-w-3xl mx-auto mb-3">
          <div className="rounded-xl border border-purple-500/30 bg-slate-800/90 p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-purple-200">GweffIT = jury verdicts for your debates. Vote in 10 seconds.</div>
                <div className="mt-1 text-xs text-slate-300">Vote fast, share with friends, and see the final verdict.</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    onClick={() => document.getElementById('trial-vote-buttons')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    className="rounded-lg bg-purple-600 hover:bg-purple-500 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    Vote now
                  </button>
                  <Link
                    to={`/about?return_to=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`}
                    className="rounded-lg bg-slate-700 hover:bg-slate-600 px-3 py-1.5 text-xs font-bold text-slate-100"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowColdTrafficBanner(false);
                  try { sessionStorage.setItem(HIDDEN_COLD_BANNER_SESSION_KEY, '1'); } catch {}
                }}
                className="text-xs text-slate-400 hover:text-slate-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto pt-2">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4 w-full max-w-full">
          <Link
            to={backHref}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium min-w-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto max-w-full min-w-0">
            <button
              onClick={handleShareTrial}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium min-w-0"
            >
              <Share2 className="w-4 h-4" />
              {isClosed ? 'Send Verdict' : 'Share'}
            </button>
            {!!user && !!row?.id && (
              <JurySummonsButton
                trial={trial}
                user={user}
                buttonLabel="Share to Crews"
                fullWidth={false}
                className="px-3 py-2 rounded-xl text-sm font-medium"
                onSummonsSent={(count) => {
                  if (process.env.NODE_ENV !== 'production') {
                    console.log('[trial] crew_share_result', { ok: true, trialId: row.id, count });
                  }
                }}
              />
            )}
            <button
              onClick={() => {
                hideTrialLocally(row.id);
                showSurfaceNotice('Hidden from your feed.');
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium min-w-0"
            >
              Hide
            </button>
            <button
              onClick={() => setShowReportModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-900/30 hover:bg-red-900/50 border border-red-700 text-red-200 text-sm font-medium min-w-0"
            >
              Report
            </button>
            {!isClosed && (
              <button
                onClick={handleDownloadCard}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium min-w-0"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            )}
          </div>
        </div>

        {/* Creator share prompt */}
        {isCreator && !isClosed && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl border-2 border-purple-400">
            <div className="flex items-center gap-2 text-white mb-3">
              <span className="text-2xl">✨</span>
              <div>
                <div className="font-black text-lg">
                  {isDecideForMe ? 'Your Decision is Live!' : 'Your Trial is Live!'}
                </div>
                <div className="text-sm text-white/90">
                  {isDecideForMe ? 'Share with your crew — they’ll help you decide.' : 'Share with friends to get votes!'}
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {user && (
                <JurySummonsButton
                  trial={trial}
                  user={user}
                  onSummonsSent={(count) => console.log(`✅ Sent ${count} summons!`)}
                />
              )}

              <button
                onClick={handleCopyLink}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
                  copied
                    ? 'bg-white text-green-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        )}

        {isSuddenDeathOpen && (
          <div className="mb-4 p-4 rounded-xl border border-amber-400/60 bg-amber-500/10">
            <div className="text-amber-300 font-black text-lg">⚡ Sudden Death</div>
            <div className="text-sm text-amber-100/90">
              Tie detected. Overtime ends in {formatTime(Math.max(0, timeLeft || 0)) || 'soon'}.
            </div>
            <div className="text-xs text-amber-200/80 mt-1">
              Next non-tie vote decides immediately (10 minute max window).
            </div>
            <div className="text-xs text-amber-200/80 mt-1">
              Live score: {votesA}-{votesB}
            </div>
          </div>
        )}

        {/* Countdown Timer */}
        {!isClosed && timeLeft != null && timeLeft > 0 && (
          <div className={`mb-4 p-4 rounded-xl border ${
            timeLeft < 30
              ? 'bg-red-900/30 border-red-500/50'
              : timeLeft < 60
                ? 'bg-yellow-900/30 border-yellow-500/50'
                : 'bg-slate-800/60 border-slate-700'
          }`}>
            <div className="flex items-center justify-center gap-2">
              <Clock className={`w-5 h-5 ${
                timeLeft < 30 ? 'text-red-400 animate-pulse' :
                timeLeft < 60 ? 'text-yellow-400' : 'text-slate-400'
              }`} />
              <span className={`font-mono font-bold text-lg ${
                timeLeft < 30 ? 'text-red-400 animate-pulse' :
                timeLeft < 60 ? 'text-yellow-400' : 'text-white'
              }`}>
                {formatTime(timeLeft)}
              </span>
              <span className="text-slate-400 text-sm">remaining</span>
            </div>
            {timeLeft < 30 && (
              <div className="mt-2 text-center text-sm text-red-300 font-semibold animate-pulse">
                ⚡ Hurry! Time is almost up!
              </div>
            )}
          </div>
        )}

        {/* Time Expired Notice */}
        {!isClosed && timeLeft === 0 && (
          <div className="mb-4 p-4 rounded-xl bg-orange-900/30 border border-orange-500/50">
            <div className="text-center">
              <div className="text-2xl mb-1">⏱️</div>
              <div className="font-bold text-orange-300">Time's Up!</div>
              <div className="text-sm text-slate-300">Waiting for verdict...</div>
            </div>
          </div>
        )}

        {/* Stakes: deciding vote + progress-to-verdict */}
        {!isClosed && jurySize > 0 && (
          <div className="mb-4 p-4 rounded-xl bg-slate-800/60 border border-slate-700">
            {window.SHOW_JURY_PROGRESS_UI !== false ? (
              <JurySeatProgress
                filled={displayedFilled}
                total={jurySize}
                trialId={id}
                onVisible={({ trialId, filled, total }) => {
                  if (window.trackEvent) {
                    window.trackEvent('jury_progress_viewed', { trial_id: trialId, filled, total });
                  }
                }}
              />
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-200">Jurors in</span>
                  <span className="text-slate-300">{displayedFilled}/{jurySize}</span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: `${progressPct}%` }} />
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  {votesRemaining === 0
                    ? 'Verdict unlocked — waiting for close.'
                    : `${votesRemaining} more vote${votesRemaining === 1 ? '' : 's'} to unlock the verdict`}
                </div>
              </>
            )}
          </div>
        )}

        {!isCreator && !isClosed && !hasVoted && isNearTie && (
          <div className="mb-4 p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl border border-yellow-400/40">
            <div className="text-center text-white">
              <div className="font-black text-lg text-yellow-100">⚖️ DEADLOCKED</div>
              <div className="text-sm text-white/90">
                It’s {votesA}-{votesB}. Your vote could break the tie.
              </div>
            </div>
          </div>
        )}

        {/* Non-creator: Vote prompt */}
        {!isCreator && !isClosed && !hasVoted && (
          <div className="mb-4 p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl border-2 border-purple-400">
            <div className="text-center text-white">
              <div className="font-black text-lg mb-1">
                {isDecideForMe ? 'Help Decide!' : 'Cast Your Vote!'}
              </div>
              <div className="text-sm text-white/90">
                {isDecideForMe ? "You're voting on a real decision. Be honest." : 'Your vote is anonymous. Choose wisely.'}
              </div>
            </div>
          </div>
        )}

        {!!visibleCrewSignals.length && !isClosed && (
          <div className="mb-4 rounded-xl border border-cyan-400/25 bg-cyan-950/20 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200/75">
              Your crews
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-100">
              {primaryCrewSignal?.yourVoteCanSwing
                ? primaryCrewSignal.liveLine
                : primaryCrewSignal?.liveLine || 'Your people already have a side in this one.'}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {visibleCrewSignals.map((crew) => (
                <span
                  key={crew.id}
                  className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-semibold text-cyan-100"
                >
                  {crew.emoji} {crew.name}
                  {crew.crewLeaderLabel ? ` · ${crew.crewLeaderLabel}` : crew.totalCrewVotes > 0 ? ' · Split' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Verdict/Trial Card */}
        <div
          ref={cardRef}
          className="rounded-2xl border border-slate-700 bg-slate-800/80 shadow-lg overflow-hidden"
        >
          {createdParam && (
            <div className="border-b border-emerald-400/25 bg-emerald-500/10 px-6 py-4">
              <div className="text-sm font-black uppercase tracking-[0.16em] text-emerald-300">Your Call Is Live</div>
              <div className="mt-1 text-base font-bold text-emerald-100">You posted it. Now start pulling in the jury.</div>
              <div className="mt-1 text-sm text-emerald-50/85">
                This is your call. Share it or send summons to start building the receipt.
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={handleShareTrial}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-emerald-400"
                >
                  <Share2 className="h-4 w-4" />
                  Share Trial
                </button>
                {!!user && !!row?.id && (
                  <JurySummonsButton
                    trial={trial}
                    user={user}
                    buttonLabel="Send Summons"
                    fullWidth={false}
                    className="px-4 py-2 rounded-xl text-sm font-bold"
                    onSummonsSent={(count) => {
                      if (process.env.NODE_ENV !== 'production') {
                        console.log('[trial] post_create_summons_sent', { trialId: row.id, count });
                      }
                    }}
                  />
                )}
              </div>
              <div className="mt-2 text-xs text-emerald-100/75">
                Come back here when the verdict lands. This is where the receipt shows up.
              </div>
            </div>
          )}
          <div className="p-6 flex items-center gap-4">
            <div className="text-6xl leading-none">{row.image_emoji || '⚖️'}</div>

            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xl font-extrabold">{row.title}</div>

                {isDecideForMe && (
                  <span className="inline-flex items-center gap-1 bg-yellow-500/20 border border-yellow-400/50 text-yellow-100 text-xs font-bold px-2 py-1 rounded-full">
                    <Zap className="w-3 h-3" />
                    Decide for me
                  </span>
                )}

                {isNew && isCreator && (
                  <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    ✨ Just Created!
                  </span>
                )}

                {isRematch && (
                  <span className="inline-flex items-center gap-1 bg-yellow-400 text-black text-xs font-bold px-2 py-1 rounded-full">
                    🔁 Rematch
                  </span>
                )}
              </div>

              {isSeededStarterTrial ? (
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-slate-300">
                    Fresh Prompt <span className="text-slate-500">• starter trial</span>
                  </div>
                </div>
              ) : creator && (
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-slate-300">
                    Created by{' '}
                    {creator.username ? (
                      <Link
                        to={`/user/${creator.username}`}
                        className="font-bold text-white hover:text-purple-300 hover:underline"
                      >
                        @{creator.username}
                      </Link>
                    ) : (
                      <span className="font-bold text-white">@{creator.id?.slice(0, 8) || 'unknown'}</span>
                    )}
                  </div>
                  {!isCreator && currentUserId && creator?.id && (
                    <FollowButton
                      currentUserId={currentUserId}
                      targetUserId={creator.id}
                      targetUsername={creator.username}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-6 pb-6">
            {/* VOTING BUTTONS - Show when trial is open and user hasn't voted */}
            {!isClosed && !hasVoted && (
              <div className="mb-6">
                {/* Creator call is already locked at creation time */}
                {!canVote ? (
                  <div className="p-4 rounded-xl bg-purple-900/30 border border-purple-500/40 text-center">
                    <div className="text-2xl mb-2">🤔</div>
                    <div className="text-purple-300 font-bold">Your call is already locked in.</div>
                    <div className="text-sm text-slate-400 mt-1">You picked your side at creation. Now bring in the jury.</div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm uppercase tracking-wide text-slate-400 mb-3">
                      {isDecideForMe ? 'Cast your verdict' : 'Cast your vote'}
                    </div>
                    <div id="trial-vote-buttons" className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleVote('A')}
                        disabled={voting}
                        className="p-4 rounded-xl border border-purple-400/50 bg-gradient-to-br from-purple-700 via-fuchsia-700 to-purple-600 text-white font-black text-lg transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 shadow-[0_18px_40px_-22px_rgba(0,0,0,0.95)]"
                      >
                        {voting ? '...' : row.side_a}
                      </button>
                      <button
                        onClick={() => handleVote('B')}
                        disabled={voting}
                        className="p-4 rounded-xl border border-amber-300/50 bg-gradient-to-br from-amber-700 via-yellow-600 to-amber-500 text-slate-950 font-black text-lg transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 shadow-[0_18px_40px_-22px_rgba(0,0,0,0.95)]"
                      >
                        {voting ? '...' : row.side_b}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-center text-slate-500">
                      🔒 Your vote is anonymous
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ALREADY VOTED - Show confirmation */}
            {!isClosed && hasVoted && (
              <div className="mb-6 p-4 rounded-xl bg-green-900/30 border border-green-500/50">
                <div className="text-center">
                  <div className="text-2xl mb-1">✅</div>
                  <div className="font-bold text-green-400">Vote Recorded!</div>
                  <div className="text-sm text-slate-300">Come back soon to see the verdict.</div>
                </div>
                {!!user && !!trial?.id && (
                  <div className="mt-3 flex justify-center">
                    <JurySummonsButton
                      trial={trial}
                      user={user}
                      buttonLabel="Share to Crews"
                      fullWidth={false}
                      className="px-3 py-2 rounded-xl text-sm font-medium"
                      onSummonsSent={(count) => {
                        if (process.env.NODE_ENV !== 'production') {
                          console.log('[trial] crew_share_result', { ok: true, trialId: row?.id || id, count, source: 'post_vote' });
                        }
                      }}
                    />
                  </div>
                )}
                {!!visibleCrewSignals.length && (
                  <div className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-950/20 px-3 py-3 text-left">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200/75">
                      Your crews
                    </div>
                    <div className="mt-1 text-sm text-cyan-50">
                      {primaryCrewSignal?.liveLine || 'Your people have a side in this one now.'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CLOSED - Show results */}
            {isClosed && (
              <div className="mt-2 space-y-4">
                {isVerdictComeback && !isVerdictLocking && (
                  <div className="rounded-xl border border-purple-500/25 bg-purple-500/10 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-200/80">
                      {comebackEyebrow}
                    </div>
                    <div className="mt-1 text-base font-bold text-white">
                      {comebackHeading}
                    </div>
                    <div className="mt-1 text-sm text-purple-100/80">
                      {comebackBody}
                    </div>
                    {isCreator && creatorCallLabel && (
                      <div className="mt-3 rounded-lg border border-purple-400/20 bg-slate-950/30 px-3 py-2 text-xs text-purple-100/85">
                        Your call: <span className="font-bold text-white">{creatorCallLabel}</span>
                        {creatorCallOutcome === 'won' && <span className="ml-2 text-emerald-300">You called it.</span>}
                        {creatorCallOutcome === 'lost' && <span className="ml-2 text-rose-300">The jury went the other way.</span>}
                        {creatorCallOutcome === 'push' && <span className="ml-2 text-slate-300">The jury deadlocked.</span>}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <div className="text-sm uppercase tracking-wide text-purple-300/80 mb-1">
                    {isVerdictLocking ? 'Jury Locked' : 'Verdict Landed'}
                  </div>
                  <div className="text-lg font-extrabold text-slate-50">
                    {isVerdictLocking
                      ? 'The jury is locked in. We&apos;re landing the ruling now.'
                      : (isVerdictComeback
                        ? (comebackRole === 'creator' ? 'Your receipt is ready.' : 'The result is ready.')
                        : 'Here&apos;s how it ended.')}
                  </div>
                </div>
                {!!visibleCrewSignals.length && (
                  <div className="rounded-xl border border-cyan-400/25 bg-cyan-950/18 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200/75">
                      Your crews
                    </div>
                    <div className="mt-1 text-sm font-semibold text-slate-100">
                      {primaryCrewSignal?.resultLine || 'Your people had a side in this verdict too.'}
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {visibleCrewSignals.map((crew) => (
                        <div key={crew.id} className="text-xs text-cyan-50/90">
                          {crew.emoji} {crew.resultLine}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {resolvedInSuddenDeath && (
                  <div className="mb-3 rounded-xl border border-amber-400/60 bg-amber-500/10 p-3 text-sm">
                    <div className="font-black text-amber-300">Won in Sudden Death</div>
                    <div className="text-amber-100/90">
                      {suddenDeathSummary?.method
                        ? `Resolved by ${String(suddenDeathSummary.method).replaceAll('_', ' ')}.`
                        : 'Resolved in overtime.'}
                    </div>
                  </div>
                )}
                {(() => {
                  if (!verdictImageUrl && !verdictLine) return null;
                  const loserLabel = winner === 'Tie'
                    ? `${row.side_a} vs ${row.side_b}`
                    : (winner === row.side_a ? row.side_b : row.side_a);
                  const hasEmbeddedVerdictText = isBuiltVerdictCardAsset(verdictImageUrl);
                  const shouldShowDeterministicImage = Boolean(verdictImageUrl);
                  const detailVerdictSummary = verdictLine || (winner ? `${winner} took this one.` : 'The result is ready.');
                  const useCompactDeterministicDetail = !hasPremiumMeme;
                  const shouldRenderDeterministicSupportSummary = useCompactDeterministicDetail && (!shouldShowDeterministicImage || !hasEmbeddedVerdictText);
                  const suppressDetailOverlayText = useCompactDeterministicDetail
                    ? Boolean(shouldShowDeterministicImage)
                    : suppressPremiumMemeLocalOverlay;
                  return (
                    <div className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900/40">
                      {shouldShowDeterministicImage && (
                        <div>
                          <VerdictMemeFrame
                            id={row.id}
                            title={row.title}
                            sideA={row.side_a}
                            sideB={row.side_b}
                            verdictLine={verdictLine || null}
                            imageUrl={verdictImageUrl}
                            emoji={row.image_emoji}
                            winnerLabel={winner || 'Tie'}
                            loserLabel={loserLabel}
                            isClose={Math.abs(pctA - 50) <= 5}
                            isLandslide={Math.abs(pctA - 50) >= 25}
                            heightClass="h-64"
                            renderState="ready"
                            showEmoji={false}
                            surface="detail-trial"
                            suppressLocalOverlayText={suppressDetailOverlayText}
                          />
                        </div>
                      )}
                      {!showPendingVerdictBanner && (
                        <div className={`${shouldShowDeterministicImage ? 'border-t border-slate-700/80 ' : ''}bg-slate-950/55 px-4 py-3`}>
                          {useCompactDeterministicDetail ? (
                            <>
                              {shouldRenderDeterministicSupportSummary && (
                                <>
                                  <div className="text-xl font-bold leading-tight text-slate-100">
                                    {row.title}
                                  </div>
                                  <div className="mt-1 text-sm text-slate-400">
                                    {row.side_a} vs {row.side_b}
                                  </div>

                                  <div className="mt-4 rounded-xl border border-slate-700/70 bg-slate-900/45 px-3 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="min-w-0 text-sm text-slate-200">
                                        {winner === 'Tie' ? (
                                          <span className="font-semibold">Tie</span>
                                        ) : (
                                          <>
                                            <span className="font-semibold text-white">{winner}</span>
                                            <span className="text-slate-400"> took it</span>
                                          </>
                                        )}
                                      </div>
                                      <div className="shrink-0 text-xs text-slate-400">{total} votes</div>
                                    </div>

                                    {total > 0 && (
                                      <div className="mt-3 space-y-2">
                                        <div className="flex items-center justify-between text-[11px] text-slate-400">
                                          <span className="truncate pr-2">{row.side_a}</span>
                                          <span>{pctA}%</span>
                                        </div>
                                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
                                          <div className="h-1.5 bg-purple-500" style={{ width: `${pctA}%` }} />
                                        </div>

                                        <div className="flex items-center justify-between text-[11px] text-slate-400">
                                          <span className="truncate pr-2">{row.side_b}</span>
                                          <span>{pctB}%</span>
                                        </div>
                                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
                                          <div className="h-1.5 bg-amber-400" style={{ width: `${pctB}%` }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </>
                          ) : (
                            !hasEmbeddedVerdictText && (
                              <div className="mb-3">
                                <div className="text-base font-bold leading-tight text-slate-100 sm:text-lg">
                                  {detailVerdictSummary}
                                </div>
                              </div>
                            )
                          )}
                          <div className="rounded-xl border border-purple-500/20 bg-purple-500/10 p-3">
                            <div className="flex flex-col gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-100">
                                  {sendBlockTitle}
                                </div>
                                <div className="mt-1 text-xs text-purple-100/80">
                                  {sendBlockBody}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                                <button
                                  onClick={handleShareTrial}
                                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2.5 text-sm font-semibold text-white hover:from-purple-500 hover:to-pink-500 sm:w-auto"
                                >
                                  <Share2 className="h-4 w-4" />
                                  {hasPremiumMeme ? 'Send the Verdict' : 'Send Verdict'}
                                </button>
                                <button
                                  onClick={handleDownloadCard}
                                  className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700"
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </button>
                                <button
                                  onClick={handleCopyLink}
                                  className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800"
                                >
                                  <Copy className="h-4 w-4" />
                                  {copied ? 'Copied' : 'Copy Link'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {winner === 'Tie' ? (
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-400/40 text-center">
                    <div className="text-2xl mb-1">🤝</div>
                    <div className="font-black text-lg text-yellow-100">TIE</div>
                    <div className="text-sm text-slate-300">The jury couldn’t decide.</div>
                    {isCreator && (
                      <button
                        onClick={() => setShowTieBreakerPrompt(true)}
                        disabled={reopeningTieBreaker}
                        className="mt-3 rounded-lg bg-amber-500/80 px-3 py-2 text-xs font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-60"
                      >
                        {reopeningTieBreaker
                          ? 'Reopening...'
                          : 'Reopen Tie-Breaker (next vote or 10m)'}
                      </button>
                    )}
                  </div>
                ) : hasPremiumMeme ? (
                  <div className={`rounded-xl ${hasPremiumMeme ? 'border border-slate-800 bg-slate-950/25 p-3' : 'p-4 bg-slate-900/30 border border-slate-700'}`}>
                    <div className={`flex items-center justify-between ${hasPremiumMeme ? 'mb-3' : 'mb-2'}`}>
                      <div className={`${hasPremiumMeme ? 'text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400' : 'font-black text-lg text-white'}`}>
                        {hasPremiumMeme ? 'Vote breakdown' : winner}
                      </div>
                      <div className={`${hasPremiumMeme ? 'text-xs text-slate-500' : 'text-sm text-slate-300'}`}>{total} votes</div>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <div className={`flex justify-between ${hasPremiumMeme ? 'text-[11px] text-slate-400 mb-1' : 'text-xs text-slate-400 mb-1'}`}>
                          <span>{row.side_a}</span>
                          <span>{pctA}%</span>
                        </div>
                        <div className={`${hasPremiumMeme ? 'h-1.5' : 'h-2'} bg-slate-700 rounded-full overflow-hidden`}>
                          <div className={`${hasPremiumMeme ? 'h-1.5' : 'h-full'} bg-purple-500`} style={{ width: `${pctA}%` }} />
                        </div>
                      </div>

                      <div>
                        <div className={`flex justify-between ${hasPremiumMeme ? 'text-[11px] text-slate-400 mb-1' : 'text-xs text-slate-400 mb-1'}`}>
                          <span>{row.side_b}</span>
                          <span>{pctB}%</span>
                        </div>
                        <div className={`${hasPremiumMeme ? 'h-1.5' : 'h-2'} bg-slate-700 rounded-full overflow-hidden`}>
                          <div className={`${hasPremiumMeme ? 'h-1.5' : 'h-full'} bg-slate-500`} style={{ width: `${pctB}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-3">
                  {isVerdictReadyForDisplay && !hasPremiumMeme && isCreator && (
                    <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-purple-200/75">
                            Optional creator upgrade
                          </div>
                          <div className="text-sm font-semibold text-white">
                            Want a louder meme card?
                          </div>
                          <div className="text-xs text-purple-100/80">
                            Same ruling, louder presentation. This makes a more dramatic share card without changing the verdict.
                          </div>
                        </div>
                        <button
                          onClick={handleGeneratePremiumMeme}
                          disabled={memeRecoveryState === 'loading'}
                          className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:opacity-60"
                        >
                          {memeRecoveryState === 'loading' ? 'Making Meme Card Upgrade…' : 'Make Meme Card Upgrade'}
                        </button>
                      </div>
                    </div>
                  )}
                  {memeRecoveryState === 'loading' && (
                    <div className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-3">
                      <div className="flex items-center gap-3">
                        <Loader2 className="h-4 w-4 animate-spin text-purple-300" />
                        <div>
                          <div className="text-sm font-semibold text-white">Making the louder meme-card version...</div>
                          <div className="text-xs text-purple-100/80">The main verdict is already ready. This only upgrades the presentation.</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {memeRecoveryState === 'succeeded' && hasPremiumMeme && (
                    <div className="rounded-xl border border-green-500/40 bg-green-900/20 p-3">
                      <div className="text-sm font-semibold text-green-200">Meme-card upgrade ready.</div>
                      <div className="text-xs text-green-100/80 mt-1">Same verdict, louder card. Now send the receipt.</div>
                    </div>
                  )}
                  {memeRecoveryState === 'failed' && (
                    <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
                      Meme-card upgrade unavailable{memeRecoveryError ? `: ${memeRecoveryError}` : ''}.
                    </div>
                  )}
                  {process.env.NODE_ENV !== 'production' && verdictSource === 'non_ai_fallback' && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-900/20 p-3">
                      <div className="text-xs text-amber-200 font-semibold">
                        AI verdict unavailable. Showing fallback verdict.
                      </div>
                      <div className="text-[11px] text-amber-300/80 mt-1">
                        Reason: {verdictReasonCode || row?.verdict_data?.fallback_reason || 'ai_error'}
                      </div>
                    </div>
                  )}
                  {showPendingVerdictBanner && (
                    <div className="rounded-xl border border-yellow-500/40 bg-yellow-900/20 p-3">
                      <div className="text-xs text-yellow-200 font-semibold">
                        {isVerdictLocking ? 'The jury is locked. The ruling is being finalized.' : 'Final verdict is still being prepared.'}
                      </div>
                      <div className="text-[11px] text-yellow-100/80 mt-1">
                        {isVerdictLocking
                          ? 'No more votes can come in. Stay here and this page will update as soon as the verdict lands.'
                          : 'We&apos;re finishing the final verdict now. This page will update when it lands.'}
                      </div>
                      {verdictReasonCode && (
                        <div className="text-[11px] text-yellow-300/80 mt-1">Reason: {verdictReasonCode}</div>
                      )}
                      <button
                        onClick={() => setLoadNonce((v) => v + 1)}
                        className="mt-2 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {!showPendingVerdictBanner && verdictRenderState === 'pending' && (
                    <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3">
                      <div className="text-xs text-slate-200 font-semibold">
                        {isVerdictLocking ? 'Ruling in progress.' : 'Verdict is being prepared.'}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {isVerdictLocking
                          ? 'The jury result is locked. We&apos;ll update this page the moment the verdict is ready.'
                          : 'We&apos;ll update this page once the verdict finishes rendering.'}
                      </div>
                      <button
                        onClick={() => setLoadNonce((v) => v + 1)}
                        className="mt-2 rounded-lg bg-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-600"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {process.env.NODE_ENV !== 'production' && (hasAuthoritativeVerdict(row) || (!isGenericFallbackPunchline(row?.verdict_data?.punchline) ? row?.verdict_data?.punchline : null)) && (
                    <div className="text-[11px] text-slate-500">
                      Source: {verdictSource || 'legacy_persisted'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isClosed && row && (
        <TrialVerdictHeroCard
          cardId="trial-detail-verdict-share-card"
          row={row}
          verdictLine={verdictLine}
          verdictImageUrl={verdictArtifactImageUrl}
          winner={winner}
          pctA={pctA}
          pctB={pctB}
          total={total}
          suppressPremiumMemeLocalOverlay={suppressPremiumMemeLocalOverlay}
          hidden
        />
      )}

      <ReportModal
        open={showReportModal}
        title="Report Trial"
        reason={reportReason}
        note={reportNote}
        submitting={reportSubmitting}
        onReasonChange={setReportReason}
        onNoteChange={setReportNote}
        onCancel={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
      />
      <ActionPromptModal
        open={showTieBreakerPrompt}
        title="Reopen tie-breaker?"
        description="Next vote decides this tied trial. Sudden death stays open for up to 10 minutes."
        inputType="none"
        submitLabel="Start sudden death"
        cancelLabel="Keep closed"
        loading={reopeningTieBreaker}
        onCancel={() => {
          if (!reopeningTieBreaker) setShowTieBreakerPrompt(false);
        }}
        onSubmit={handleReopenTieBreaker}
      />

      {/* Claim Vote Modal - shown after guest vote */}
      {showClaimModal && (
        <ClaimVoteModal
          onClose={() => setShowClaimModal(false)}
          onSuccess={() => {
            setShowClaimModal(false);
            // Auth state change will handle the rest (claiming votes, redirect)
          }}
          trialTitle={row?.title}
          voteSide={lastVoteSide}
          mode={claimModalMode}
        />
      )}
    </div>
  );
}
